import { AsyncPipe, DatePipe, NgClass, formatDate } from "@angular/common";
import { AfterViewInit, ChangeDetectorRef, Component, ComponentRef, ElementRef, EventEmitter, LOCALE_ID, NgZone, OnDestroy, OnInit, Output, Signal, TemplateRef, ViewChild, ViewContainerRef, WritableSignal, computed, effect, inject, input, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { IgxActionStripComponent, IgxCheckboxComponent, IgxDropDownComponent as IgxDropDownComponent_1, IgxDropDownItemComponent, IgxDropDownItemNavigationDirective, IgxForOfDirective, IgxGridComponent as IgxGridComponent_1, IgxToggleActionDirective } from "@infragistics/igniteui-angular";
import { ColumnType, DefaultSortingStrategy, FilteringLogic, FilteringStrategy, IColumnPipeArgs, IFilteringExpressionsTree, IFilteringStrategy, IGridSortingStrategy, IGroupingExpression, IgxBooleanFilteringOperand, NoopFilteringStrategy, NoopSortingStrategy, SortingDirection } from '@infragistics/igniteui-angular/core';
import { IgxDropDownComponent } from '@infragistics/igniteui-angular/drop-down';
import { CellType, CsvFileTypes, GridPagingMode, GridSelectionMode, IActiveNodeChangeEventArgs, IColumnExportingEventArgs, IGridCellEventArgs, IGridEditDoneEventArgs, IGridEditEventArgs, IGridKeydownEventArgs, IGridScrollEventArgs, IGridState, IGridStateOptions, IGridToolbarExportEventArgs, IPinColumnEventArgs, IRowExportingEventArgs, IRowSelectionEventArgs, IgxCellEditorTemplateDirective, IgxCellHeaderTemplateDirective, IgxCellTemplateDirective, IgxColumnComponent, IgxCsvExporterOptions, IgxCsvExporterService, IgxExcelExporterService, IgxExcelStyleClearFiltersComponent, IgxExcelStyleColumnOperationsTemplateDirective, IgxExcelStyleConditionalFilterComponent, IgxExcelStyleFilterOperationsTemplateDirective, IgxExcelStyleHeaderComponent, IgxExcelStyleHeaderIconDirective, IgxExcelStyleSearchComponent, IgxGridCell, IgxGridExcelStyleFilteringComponent, IgxGridStateDirective, IgxGridToolbarComponent, IgxGridToolbarHidingComponent, IgxGridToolbarPinningComponent, IgxNumberSummaryOperand, IgxRowSelectorDirective, RowType } from '@infragistics/igniteui-angular/grids/core';
import { IgxGridComponent, IgxGridGroupByAreaComponent } from '@infragistics/igniteui-angular/grids/grid';
import { IgxPaginatorComponent, IgxPaginatorContentDirective } from "@infragistics/igniteui-angular/paginator";
import { ClassDirective } from "@ngbracket/ngx-layout/extended";
import { FlexDirective, LayoutAlignDirective, LayoutDirective, FlexFillDirective } from "@ngbracket/ngx-layout/flex";
import { Command, CommandBarComponent, CommandMenuComponent, LabelCommand } from "@tylertech/commands-module";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { KeyHandlerService, OmniBarService, SecurityService } from "@tylertech/core-module";
import { IMenuOption, IconButtonVariant, PopoverComponent } from "@tylertech/forge";
import { ForgeBadgeModule, ForgeButtonModule, ForgeCardModule, ForgeChipModule, ForgeExpansionPanelModule, ForgeFocusIndicatorModule, ForgeIconButtonModule, ForgeIconModule, ForgeOpenIconModule, ForgePopoverProxyModule, ForgeScaffoldModule, ForgeSkeletonModule, ForgeTextFieldModule, ForgeToolbarModule, ForgeTooltipModule } from "@tylertech/forge-angular";
import { Utilities } from "@tylertech/shared-module";
import { ClassMetadata, GridEditType, IGridViewMetadata, IMetadataAction, Key, MetadataAction, MetadataSummary, ModifierKey, PropertyMetadata, SelectionMode } from "@tylertech/tyler-api-types";
import { BehaviorSubject, Observable, Subject, Subscription, fromEvent } from "rxjs";
import { debounceTime, filter, take, takeUntil, tap, } from "rxjs/operators";
import { AugmentedModelObject, RepositoryFormData, RowCommandArgs, RowCommandRow, TableRowSelectArgs } from "../../../interfaces";
import { GridCurrencyPipe } from "../../../pipes/gridCurrency.pipe";
import { GridNumberPipe } from "../../../pipes/gridNumber.pipe";
import { CellEditService, GridActionService, GridCellStateService, GridDeleteService, GridEntityService, GridEventService, GridExportService, GridFetchType, GridFilterService, GridFormatService, GridLoadService, GridLookupService, GridMetadataService, GridProcessingService, GridQueryService, GridSelectionEventArg, GridSelectionService, GridSortService, GridSummaryService, GridValidationService, IExportColumnConfiguration, InfoGridLayoutService, MetadataActionService, MetadataEventService, MetadataInputTypeHelperService, MetadataPropertyService, MetadataService, MetadataTableEventArg, MetadataViewService, MultipleEntityEditService, ProviderStorageService } from "../../../services";
import { MetadataActionHelpers, ObjectHelpers } from "../../../utilities";
import { MetadataInputComponent } from "../../metadataInput/metadataInput.component";
import { CellEditOverlay } from "./cellEditOverlay.component";
import { ColumnMoveSelectorComponent } from "./columnMoveSelector.component";
import { ContextMenuAction, GridContextComponent } from "./gridContext.component";
import { GridFilterComponent } from "./gridFilter.component";
import { GridSearchComponent } from "./gridSearch.component";
import { GroupedGridSortingStrategy } from "./groupedGridSortingStrategy";
import { RowActionsComponent } from "./rowActions.component";
import { Average, CurrencyAverage, CurrencySummary, Summary } from "./summary";

export interface GridRowSetting
{
    UserId: string;
    Title: string;
    Path: string;
    Rows: number;
}

interface SummarySetting
{
    HasSummary: boolean;
    SummaryType: MetadataSummary;
}

@Component({
    selector: "info-grid",
    templateUrl: "./infoGrid.tmpl.html",
    imports: [
        AsyncPipe,
        CellEditOverlay,
        ColumnMoveSelectorComponent,
        CommandBarComponent,
        CommandMenuComponent,
        DatePipe,
        ClassDirective,
        FlexDirective,
        LayoutAlignDirective,
        LayoutDirective,
        FlexFillDirective,
        ForgeBadgeModule,
        ForgeButtonModule,
        ForgeCardModule,
        ForgeChipModule,
        ForgeExpansionPanelModule,
        ForgeFocusIndicatorModule,
        ForgeIconButtonModule,
        ForgeIconModule,
        ForgeOpenIconModule,
        ForgePopoverProxyModule,
        ForgeScaffoldModule,
        ForgeSkeletonModule,
        ForgeTextFieldModule,
        ForgeToolbarModule,
        ForgeTooltipModule,
        FormsModule,
        GridCurrencyPipe,
        GridFilterComponent,
        GridNumberPipe,
        GridSearchComponent,
        IgxActionStripComponent,
        IgxCellEditorTemplateDirective,
        IgxCellHeaderTemplateDirective,
        IgxColumnComponent,
        IgxCellTemplateDirective,
        IgxCheckboxComponent,
        IgxColumnComponent,
        IgxDropDownComponent_1,
        IgxDropDownItemComponent,
        IgxDropDownItemNavigationDirective,
        IgxExcelStyleClearFiltersComponent,
        IgxExcelStyleColumnOperationsTemplateDirective,
        IgxExcelStyleConditionalFilterComponent,
        IgxExcelStyleFilterOperationsTemplateDirective,
        IgxExcelStyleHeaderComponent,
        IgxExcelStyleHeaderIconDirective,
        IgxExcelStyleSearchComponent,
        IgxForOfDirective,
        IgxGridComponent_1,
        IgxGridExcelStyleFilteringComponent,
        IgxGridGroupByAreaComponent,
        IgxGridStateDirective,
        IgxGridToolbarComponent,
        IgxGridToolbarHidingComponent,
        IgxGridToolbarPinningComponent,
        IgxPaginatorComponent,
        IgxPaginatorContentDirective,
        IgxRowSelectorDirective,
        IgxToggleActionDirective,
        MetadataInputComponent,
        NgClass,
        ReactiveFormsModule,
        RowActionsComponent,
    ],
})
export class InfoGridComponent implements OnInit, AfterViewInit, OnDestroy
{
    private cdr = inject(ChangeDetectorRef);
    private ngZone = inject(NgZone);
    private csvExportService = inject(IgxCsvExporterService);
    private excelExportService = inject(IgxExcelExporterService);
    private $omniBarService = inject(OmniBarService);
    private $commonDialogsService = inject(CommonDialogsService);
    private readonly $metadataViewService = inject(MetadataViewService);
    private readonly $metadataService = inject(MetadataService);
    private $metadataActionService = inject(MetadataActionService);
    private $metadataEventService = inject(MetadataEventService);
    private $gridEventService = inject(GridEventService);
    private $providerStorageService = inject(ProviderStorageService);
    private $metadataInputTypeHelperService = inject(MetadataInputTypeHelperService);
    locale = inject(LOCALE_ID);
    private $securityService = inject(SecurityService);
    private readonly $metadataPropertyService = inject(MetadataPropertyService);
    private $multipleEntityEditService = inject(MultipleEntityEditService);
    private $gridCellStateService = inject(GridCellStateService);
    private $gridProcessingService = inject(GridProcessingService);
    private $gridMetadataService = inject(GridMetadataService);
    private $gridEntityService = inject(GridEntityService);
    private $gridExportService = inject(GridExportService);
    private $gridFormatService = inject(GridFormatService);
    private $gridLoadService = inject(GridLoadService);
    private $gridLookupService = inject(GridLookupService);
    private $gridLayoutService = inject(InfoGridLayoutService);
    private $gridActionService = inject(GridActionService);
    private $gridQueryService = inject(GridQueryService);
    private $gridSelectionService = inject(GridSelectionService);
    private $gridSortService = inject(GridSortService);
    private $gridSummaryService = inject(GridSummaryService);
    private $gridFilterService = inject(GridFilterService);
    private $gridDeleteService = inject(GridDeleteService);
    private $cellEditService = inject(CellEditService);
    private $gridValidationService = inject(GridValidationService);
    private $keyHandlerService = inject(KeyHandlerService);

    readonly multipleEdit = input<boolean>();
    readonly displayHeader = input<boolean>(true);
    readonly useRepository = input<boolean>();
    readonly noFilter = input<boolean>();
    readonly viewMetadata = input.required<IGridViewMetadata>();
    readonly model = input<any>();
    readonly datacontroller = input<RepositoryFormData>();
    readonly disableViewAction = input<boolean>();
    readonly parentView = input<string>();
    readonly isStepperGrid = input<boolean>();
    readonly disableGridAdd = input<boolean>(false);
    readonly viewOnly = input.required<boolean>();

    @Output()
    repositoryFunction = new EventEmitter<RowCommandArgs>();

    @Output()
    onSelected = new EventEmitter<TableRowSelectArgs>();

    @ViewChild("grid") grid: IgxGridComponent;
    @ViewChild("toolbar") toolbar: IgxGridToolbarComponent;
    @ViewChild("gridLayoutDropdown") gridLayoutDropdown: IgxDropDownComponent;
    @ViewChild("cellEditTemplate") cellEditTemplate: TemplateRef<any>;
    @ViewChild(IgxGridStateDirective, { static: true }) public state: IgxGridStateDirective;
    @ViewChild("cellEditInput") private cellEditInput: ElementRef;
    @ViewChild("cellEditInlinePopover") cellEditInlinePopover: PopoverComponent;
    @ViewChild("cellEditPopupPopover") cellEditPopupPopover: PopoverComponent;
    @ViewChild("cellEditPopupInput", { read: ElementRef }) cellEditPopupInput: ElementRef;

    public options: IGridStateOptions = {
        columns: true,
        filtering: true,
        advancedFiltering: true,
        sorting: true,
        cellSelection: false,
        rowSelection: false,
        columnSelection: false,
        paging: false,
        groupBy: false,
        expansion: false,
        rowPinning: false,
        moving: false
    };

    public filterStrategy: IFilteringStrategy;

    get sortStrategy(): IGridSortingStrategy
    {
        //a groupable grid has all of its data, so it lets igx sort locally; everything else is sorted by the server
        if (this.isGroupedGrid)
            return undefined;
        return NoopSortingStrategy.instance();
    }

    private readonly destroy$ = new Subject<void>();

    private afterFetchCallback: (() => void) | null = null;

    providerId: string;

    commands: Array<Command> = [];

    runRecalculate: boolean = false;

    clearSkipLayout: boolean = false;

    public data: Observable<any[]>;

    public remoteData: BehaviorSubject<any[]>;

    private _prevRequest: Subscription;

    public mode = GridPagingMode.Remote;

    delayUpdate: boolean = false;

    cellEditMetadata: ClassMetadata;

    currentEditCellOriginalModelValue: any;

    currentEditCellOriginalDisplayValue: any;

    currentEditCellPropertyName: string;

    recalculatedDuringEdit: boolean;

    initialWidths: Record<string, string> = {};

    checkForLayout: boolean = true;

    layoutLoaded: boolean;

    summarySetup: Record<string, SummarySetting> = {};

    private verticalScrollListener: EventListener;
    private verticalScrollElement: HTMLElement;

    private readonly recalculating: WritableSignal<boolean> = signal(false);
    private readonly runCheckboxEdit = signal<(() => void) | null>(null);
    readonly fetching = signal(true);
    readonly isAdding = signal(false);
    readonly canAdd = signal(false);
    readonly showToolbar = signal(false);
    readonly initialized = signal(false);
    readonly grouping = signal(false);
    readonly hasSelection = signal(false);
    readonly isMassDeleting = signal(false);
    readonly hasCommands = signal(false);
    readonly allowSort = signal(false);
    readonly selectionMode = signal<GridSelectionMode>(GridSelectionMode.none);
    readonly totalCount = signal(0);
    readonly tableOptionsOpen = signal(true);
    readonly restoringLayout = signal(false);
    readonly recentLoaded = signal(false);
    readonly metadataServerPaging = signal(false);
    readonly alternateGridLines = signal(false);
    readonly isGridCellEdit = signal(false);
    readonly columns = signal<any[]>([]);
    readonly cellEditInPopup = signal(false);
    readonly cellEditTransition = signal(false);
    readonly cellEditModel = signal<any>(undefined);
    readonly refreshAction = signal(false);
    readonly filterHeight = signal("");
    readonly allowShowRecent = signal(false);

    editFirstCellAfterAdd: boolean = false;

    cellEditAddWaitForRecalculate: boolean = false;

    cellEditNextEditableEnabledChange: boolean = false;

    gridOverwritesCompleted = false;

    holdScrollPosition: boolean;

    //Set from the moment cellEditAfterAdd starts moving to the new row until activation finishes, which spans isAdding clearing and
    //cellEditing starting; a fetch landing in that window would otherwise refetch on the sort it just reset, then restore the old scroll
    //position and push the new row out of view under the cell editor.
    activatingCellEdit: boolean = false;

    tabbedViewGrid: boolean;

    private modelsToDelete: any[];

    private isDeleteRetrying: boolean = false;

    private indexToDelete: number;

    cellClasses: any;

    readonly columnValuesStrategy: (column: ColumnType, columnExprTree: IFilteringExpressionsTree, done: (uniqueValues: any[]) => void) => void;

    public clipboardOptions = {
        enabled: true,
        copyHeaders: false,
        copyFormatters: true,
        separator: '\t'
    };

    constructor()
    {
        Summary.gridFormatService = this.$gridFormatService;
        CurrencySummary.gridFormatService = this.$gridFormatService;
        this.remoteData = new BehaviorSubject([]) as any;

        this.ngZone.runOutsideAngular(() =>
        {
            fromEvent(window, "resize", { passive: true })
                .pipe(debounceTime(150), takeUntilDestroyed())
                .subscribe(() => this.setGridHeight());
        });

        this.columnValuesStrategy = this.columnValuesStrategyImpl.bind(this);

        this.$gridDeleteService.deleteRetry.pipe(takeUntilDestroyed()).subscribe(() => this.processNextDelete());

        this.registerListeners();
    }

    private readonlyCellCache = new Map<string, boolean>();

    setCellClasses(): void
    {
        this.readonlyCellCache.clear();
        this.cellClasses = {
            'invalid-cell': this.invalidCellCondition,
            'readonly-cell': this.readOnlyCellEditCell
        };
    }

    private invalidCellCondition = (rowData: any, columnKey: any): boolean =>
    {
        return rowData["$$model"]["$cellEditInvalidProperties"]?.includes(columnKey) ?? false;
    };

    private readOnlyCellEditCell = (rowData: any, columnKey: string): boolean =>
    {
        if (!this.isGridCellEdit())
            return false;
        const cacheKey = `${rowData.Id}|${columnKey}`;
        const cached = this.readonlyCellCache.get(cacheKey);
        if (cached !== undefined)
            return cached;
        const cell = this.grid.getCellByKey(rowData.Id, columnKey);
        const viewOnly = this.$cellEditService.ViewOnlyCell(cell, this.grid, this.$providerStorageService.GridData[this.providerId], this.viewMetadata(), this.columns());
        this.readonlyCellCache.set(cacheKey, viewOnly);
        return viewOnly;
    };

    private readonly toolbarEffect = effect(() =>
    {
        if (this.showToolbar())
            requestAnimationFrame(() => this.applyToolbarOverwrites());
    });

    applyToolbarOverwrites(): void
    {
        if (this.gridOverwritesCompleted)
            return;
        const root = this.grid.nativeElement as HTMLElement;
        const pinButton = root.querySelector("igx-grid-toolbar-pinning button");
        if (Utilities.IsNullOrUndefined(pinButton))
            return;

        this.gridOverwritesCompleted = true;

        pinButton.setAttribute("tabindex", "-1");
        root.querySelector("igx-grid-toolbar-hiding button")?.setAttribute("tabindex", "-1");

        const gridBody = root.querySelector(".igx-grid__tbody-content");
        if (gridBody)
            fromEvent(gridBody, "blur")
                .pipe(takeUntil(this.destroy$))
                .subscribe(() => this.grid.clearCellSelection());

        root.querySelector(".igx-grid__tfoot div")?.removeAttribute("tabindex");
        root.querySelector("igx-virtual-helper.igx-vhelper--vertical")?.setAttribute("tabindex", "-1");
    }

    ngOnInit(): void
    {
        this.providerId = this.viewMetadata().GridProperties["$providerId"];
        this.metadataServerPaging.set(this.$providerStorageService.GridFetchType[this.providerId] === GridFetchType.ServerStore);
        this.setFilterStrategy();
        this.data = this.remoteData.asObservable();
        this.tableOptionsOpen.set(!this.viewMetadata().GridProperties.CloseOptionsDefault);
        this.allowShowRecent.set(this.$gridFilterService.allowShowRecent(this.viewMetadata()) && this.$gridFilterService.hasRecent(this.viewMetadata().GridId) && !this.recentLoaded());

        if (this.$metadataViewService.IsCellEditGrid(this.viewMetadata()))
        {
            this.$metadataEventService.onValidateCellGrid.pipe(takeUntil(this.destroy$)).subscribe(() =>
            {
                this.$gridValidationService.validateCellEditGrid(this.providerId, this.viewMetadata(), this.cellEditing);
                this.grid?.markForCheck();
            });
        }

        this.alternateGridLines.set(this.$securityService.UseAlternatingGridLines);
        this.filterHeight.set(this.metadataServerPaging() ? "234px" : "380px");
        this.isGridCellEdit.set(this.viewMetadata()?.EditType === GridEditType.Cell);
    }

    registerListeners(): void
    {
        const self = this;

        self.$metadataEventService.onRecalculateWorking.pipe(takeUntilDestroyed())
            .subscribe(args =>
            {
                self.recalculating.set(args.Recalculating);

                let model = self.datacontroller().Entity;
                if (self.viewMetadata().SubPropertyType && self.datacontroller().OtherEntities[self.viewMetadata().SubPropertyType])
                    model = self.datacontroller().OtherEntities[self.viewMetadata().SubPropertyType][0];
                else if (self.$cellEditService.SubPropertyIsOwnedByParent(model, self.cellEditModel(), self.viewMetadata().SubPropertyName))
                    model = self.cellEditModel()?.["$$Parent"];

                if (self.cellEditing && !self.recalculating() && self.viewMetadata().SubPropertyName)
                {
                    let row;
                    const chain = self.viewMetadata().SubPropertyName.split(".");
                    if (chain.length > 1)
                    {
                        const subModel = self.viewMetadata().SubPropertyName.split('.').reduce((current, key) => current?.[key], model);
                        if (subModel)
                            row = subModel[self.currentRowIndex];
                    }
                    else if (model[self.viewMetadata().SubPropertyName])
                        row = model[self.viewMetadata().SubPropertyName][self.currentRowIndex];

                    if (row)
                    {
                        if (row["$added"] && row["$pristine"])
                            self.leaveCellEdit(true);
                        else
                        {
                            const newValue = row[self.currentEditCellPropertyName];
                            if (newValue !== self.currentEditCellOriginalModelValue)
                            {
                                self.currentEditCellOriginalModelValue = newValue;
                                self.recalculatedDuringEdit = true;
                            }
                        }

                    }
                    else
                        self.endCellEditFinal();


                }

                if (!self.recalculating())
                {
                    self.$gridValidationService.validateCellEditGrid(self.providerId, self.viewMetadata(), self.cellEditing);
                    const fn = this.runCheckboxEdit();
                    if (fn)
                    {
                        this.runCheckboxEdit.set(null);
                        fn();
                    }
                }

                if (!self.cellEditAddWaitForRecalculate)
                    return;

                self.cellEditAddWaitForRecalculate = false;

                self.viewMetadata().GridProperties.CanAdd = self.$metadataService.GetPermission(
                    self.viewMetadata().GridProperties["$InitialCanAdd"],
                    self.viewMetadata().CanAddProperty,
                    model
                );
                self.canAdd.set(self.viewMetadata().GridProperties.CanAdd);
                if (self.$cellEditService.IsLastEditableColumn(self.grid, self.viewMetadata(), self.currentRowIndex, self.currentColumnIndex) && self.viewMetadata().GridProperties.CanAdd)
                    self.addItem(false);
                else
                    self.cellEditNextEditableEnabledChange = true;
            });


        this.$gridEventService.onUpdateProjectionGrid.pipe(takeUntilDestroyed())
            .subscribe(projectionArg =>
            {
                if (projectionArg.ProviderId === self.providerId)
                    self.$gridEntityService.updateProjectionTableEntity(projectionArg.ProviderId, projectionArg.Entities, self.viewMetadata())
                        .then(() =>
                        {
                            self.$providerStorageService.BuildSummaries[self.providerId] = true;
                            requestAnimationFrame(() => self.afterFetch(false, false, false));
                        });
            });

        this.$gridEventService.onTableEntityChanged.pipe(takeUntilDestroyed())
            .subscribe(provider =>
            {
                if (provider === self.providerId)
                {
                    self.summarySetup = {};
                    self.fetching.set(true);
                    self.$cellEditService.ViewOnly = {};
                    if (!self.isGridCellEdit())
                    {
                        self.resetScrollPosition();
                    }

                    const initial = self.grid.data.length === 0 || self.$providerStorageService.ApplyFilter[self.providerId] || self.$providerStorageService.ClearFilter[self.providerId];

                    self.fetch(initial, false, true, true);
                }
            });

        this.$gridEventService.onTableLoadingFinished.pipe(takeUntilDestroyed())
            .subscribe(provider =>
            {
                if (provider === self.providerId)
                {
                    self.fetching.set(false);
                    self.cellEditing = false;
                    self.isAdding.set(false);
                }
            });

        this.$gridEventService.onFetchTable.pipe(takeUntilDestroyed())
            .subscribe(() =>
            {
                self.fetching.set(true);
                self.fetch(true, false, true);
            });

        this.$gridEventService.onUpdateGridSelection.pipe(takeUntilDestroyed())
            .subscribe((arg: GridSelectionEventArg) =>
            {
                if (arg.Clear)
                {
                    self.$gridSelectionService.SetSelectedIds(arg.ProviderId, []);
                }
                else if (arg.ProviderId === self.providerId)
                {
                    self.$gridSelectionService.SetSelected(
                        self.providerId,
                        self.grid,
                        true,
                        self.viewMetadata(),
                        self.$gridSelectionService.AllSelected[self.providerId],
                        self.$gridSelectionService.AllDeselected[self.providerId]
                    );
                }
            });
        this.$gridEventService.onGridResize.pipe(takeUntilDestroyed())
            .subscribe(() =>
            {
                setTimeout(() => self.setGridHeight(), 250);
            });

        this.$gridEventService.onCellEditOnAdd.pipe(takeUntilDestroyed())
            .subscribe(providerId =>
            {
                if (providerId === self.providerId)
                    self.cellAdding = true;
            });

        this.$gridEntityService.onModelChanged.pipe(takeUntilDestroyed())
            .subscribe(() =>
            {
                self.$gridCellStateService.computeAllCellStates(self.providerId, self.grid, self.columns());
            });

        this.$gridLookupService.onLookupLoaded.pipe(takeUntilDestroyed())
            .subscribe((type: string) =>
            {
                if (self.columns()?.some(c => c.LookupType === type))
                    self.grid.cdr.detectChanges();
            });
    }

    public ngAfterViewInit(): void
    {
        if (this.viewMetadata())
        {
            //Reset viewMetadata $$cellEditing if the user managed to leave the grid without exiting edit mode.  Hopefully not a long term problem
            this.viewMetadata()["$$cellEditing"] = this.cellEditing;
            this.tabbedViewGrid = this.viewMetadata()["$tabbedViewGrid"];
        }
        if (this.viewMetadata() && this.viewMetadata()["$projectionGridUpdatingEntity"])
        {
            this.viewMetadata()["$columns"] = this.$gridProcessingService.defineColumns(
                [this.viewMetadata()["$projectionGridUpdatingEntity"]],
                this.viewMetadata()
            );

            this.$gridEntityService.updateProjectionTableEntity(this.providerId, [this.viewMetadata()["$projectionGridUpdatingEntity"]], this.viewMetadata())
                .then(() => this.fetch(true, false, true));
        }
        else if (this.$gridQueryService.hasQuery && this.model().$applyProperty)
        {
            this.$gridQueryService.buildFilterQueryParam(this.viewMetadata());
            this.applyPromptFilter();
        }
        else
            this.fetch(true, false, true);

        this.verticalScrollElement = this.grid?.nativeElement?.querySelector(".igx-vhelper--vertical") as HTMLElement;
        this.verticalScrollListener = () =>
        {
            this.$providerStorageService.LastScrollPosition[this.providerId] = this.verticalScrollElement.scrollTop;

            if (this.fetching() || this.$providerStorageService.Scrolling[this.providerId])
                return;
            const gridData = this.$providerStorageService.GridData[this.providerId];
            const hasMore = gridData?.length < this.$providerStorageService.TotalHits[this.providerId];
            if (!hasMore)
                return;

            if (this.verticalScrollElement.scrollTop + this.verticalScrollElement.clientHeight >= this.verticalScrollElement.scrollHeight - 1)
                this.handlePreLoadImpl();
        };
        if (this.verticalScrollElement)
            this.verticalScrollElement.addEventListener("scroll", this.verticalScrollListener);

        this.syncVirtualScrollOnSettle();

        this.grid.filteringExpressionsTreeChange
            .pipe(
                tap(() => this.fetching.set(true)),
                debounceTime(150),
                takeUntil(this.destroy$)
            )
            .subscribe(() =>
            {
                this.$gridLayoutService.SettingState[this.providerId] = false;
                this.$gridLoadService.FilterChanged[this.providerId] = true;
                this.runRecalculate = true;
                this.$gridSelectionService.AllSelected[this.providerId] = false;
                this.$gridSelectionService.AllDeselected[this.providerId] = false;
                this.$gridSelectionService.Selecting[this.providerId] = false;
                this.fetch(false, false, true);
            });
        this.grid.sortingExpressionsChange.pipe(debounceTime(150), takeUntil(this.destroy$))
            .subscribe(() =>
            {
                if (this.$gridLayoutService.SettingState[this.providerId])
                {
                    this.$gridLayoutService.SettingState[this.providerId] = false;
                    return;
                }
                this.$gridLoadService.FilterChanged[this.providerId] = true;
                if (this.skipSort)
                    return;
                this.fetching.set(true);
                this.$gridSelectionService.Selecting[this.providerId] = false;
                this.fetch(false, false, true);
            });
        this.grid.dataPreLoad.pipe(takeUntil(this.destroy$))
            .subscribe(($event) =>
            {
                if (this.fetching() || this.$providerStorageService.Scrolling[this.providerId] || this.grid.groupingResult?.length > 0 || this.holdScrollPosition)
                    return;
                const scrollDown = this.scrollStartIndex < $event.startIndex;
                this.scrollStartIndex = $event.startIndex;
                const gridData = this.$providerStorageService.GridData[this.providerId];
                const isLastChunk = (this.grid.virtualizationState.startIndex + this.grid.virtualizationState.chunkSize) >= gridData.length;
                const hasMore = gridData.length < this.$providerStorageService.TotalHits[this.providerId];
                if (scrollDown && isLastChunk && hasMore)
                    this.handlePreLoadImpl();
            });
        this.grid.groupingExpressionsChange.pipe(takeUntil(this.destroy$))
            .subscribe((expressions) =>
            {
                if (Utilities.IsNullOrUndefinedOrEmpty(expressions))
                {
                    this.grouping.set(false);
                    return;
                }

                this.applyGroupSortStrategy(expressions);

                if (this.fetching() || this.entireGridLoaded || this.restoringLayout())
                    return;
                this.grouping.set(true);
                this.fetching.set(true);
                this.$gridSelectionService.Selecting[this.providerId] = false;
                this.fetch(false, false, true, false, true);
                this.entireGridLoaded = true;
            });
        this.grid.groupingDone.pipe(takeUntil(this.destroy$)).subscribe(() => this.grouping.set(false));
    }

    scrollStartIndex: number = 0;
    entireGridLoaded: boolean = false;
    private groupIndicatorObserver: MutationObserver;
    private groupIndicatorListeners: Array<{ element: HTMLElement; handler(event: KeyboardEvent): void }> = [];
    private activeHeaderField: string;

    get combinedViewTable(): boolean
    {
        return this.viewMetadata()["$combinedViewTable"];
    }

    setGridHeight(): void
    {
        const minMaxHeight = 400;
        let isDialogGrid = false;

        if (this.tabbedViewGrid)
        {
            let parent: HTMLElement = document.querySelector(".dialog-content");
            if (Utilities.IsNullOrUndefined(parent))
            {
                parent = document.querySelector(`#${this.parentView()}`) as HTMLElement;
            }

            const childViews = parent.querySelectorAll(".combined-view--view:not(.combined-view--table-view)");
            const padding = 32;

            setTimeout(() =>
            {
                let siblingsHeight = 0;
                childViews.forEach(child =>
                {
                    if (child.querySelectorAll("forge-tab-bar").length === 0)
                    {
                        siblingsHeight += Math.ceil(child.getBoundingClientRect().height);
                    }
                });

                const tabBar = parent.querySelector("forge-tab-bar");
                if (!Utilities.IsNullOrUndefined(tabBar))
                {
                    siblingsHeight += tabBar.getBoundingClientRect().height;
                }

                const toolBar = parent.querySelector("forge-toolbar");
                if (!Utilities.IsNullOrUndefined(toolBar))
                {
                    siblingsHeight += toolBar.getBoundingClientRect().height;
                }

                let newHeight = parent.clientHeight - siblingsHeight - padding;
                if (newHeight < minMaxHeight)
                {
                    newHeight = minMaxHeight;
                }
                this.grid.nativeElement.parentElement.style.height = `${newHeight}px`;
            }, 100);

            return;
        }

        if (this.combinedViewTable)
        {
            let parent: HTMLElement = document.querySelector(".dialog-content");
            if (Utilities.IsNullOrUndefined(parent))
            {
                parent = document.querySelector(`#${this.parentView()}`) as HTMLElement;
            }
            else
            {
                isDialogGrid = true;
            }

            let tabPanelHeight = 0;

            if (!Utilities.IsNullOrUndefined(parent))
            {
                const childViews = parent.querySelectorAll(".combined-view--view:not(.combined-view--table-view)");
                const padding = 32;

                const tables = parent.querySelectorAll(".combined-view--table-view");

                if (onlyOneValidTable(tables) || this.isGridCellEdit())
                {
                    setTimeout(() =>
                    {
                        let siblingsHeight = 0;
                        childViews.forEach(child =>
                        {
                            if (child.querySelectorAll(".forge-view-switcher__view--hidden").length === 0)
                            {
                                siblingsHeight += Math.ceil(child.getBoundingClientRect().height);
                            }
                            else
                            {
                                if (tabPanelHeight === 0)
                                {
                                    tabPanelHeight = parent.querySelector("forge-tab-bar").clientHeight;
                                }
                            }
                        });

                        if (siblingsHeight === 0)
                        {
                            const header = parent.querySelector("forge-toolbar");
                            if (header)
                            {
                                siblingsHeight = header.clientHeight;
                            }
                        }

                        let newHeight = parent.clientHeight - siblingsHeight - padding - tabPanelHeight;
                        if (newHeight < minMaxHeight)
                        {
                            newHeight = minMaxHeight;
                        }
                        this.grid.nativeElement.parentElement.style.height = `${newHeight}px`;
                    }, 100);
                }
                else
                {
                    const primaryViewWidth = document.querySelector("metadata-view")?.clientWidth;

                    if ((isDialogGrid && tables.length > 1) || this.grid.nativeElement.clientWidth <= primaryViewWidth / 2)
                    {
                        this.grid.nativeElement.parentElement.style.height = "100%";
                        this.grid.nativeElement.parentElement.style.maxHeight = "400px";
                    }
                    else
                    {
                        const rowHeight = this.grid.rowHeight + 1; //Add 1 for padding
                        const rowCount = this.grid.totalRowsCountAfterFilter + 1; //Add 1 for header row
                        const summaryRowHeight = this.grid.summaryRowHeight;
                        const headerPadding = 48;
                        const scrollPadding = 20;
                        const footerPadding = 44;

                        let newHeight = rowHeight * rowCount + summaryRowHeight + headerPadding + scrollPadding + footerPadding;

                        if (newHeight > minMaxHeight)
                        {
                            newHeight = minMaxHeight;
                        }

                        this.grid.nativeElement.parentElement.style.height = `${newHeight}px`;
                    }
                }
            }
        }
        else if (this.isStepperGrid())
        {
            const parent: HTMLElement = document.querySelector(`#${this.parentView()}`).parentElement.parentElement.parentElement as HTMLElement;

            if (!Utilities.IsNullOrUndefined(parent))
            {
                const padding = 32;
                setTimeout(() =>
                {
                    let newHeight = parent.clientHeight - padding;
                    if (newHeight < minMaxHeight)
                    {
                        newHeight = minMaxHeight;
                    }
                    this.grid.nativeElement.parentElement.style.height = `${newHeight}px`;
                }, 100);
            }
        }
        else
        {
            this.grid.nativeElement.parentElement.style.height = "100%";
        }

        function onlyOneValidTable(tables: NodeListOf<Element>): boolean
        {
            let tableCount = 0;

            tables.forEach(table =>
            {
                if (table.clientHeight > 0)
                {
                    tableCount++;
                }
            });

            return tableCount === 1;
        }
    }

    setFilterStrategy(): void
    {
        if (this.metadataServerPaging()) this.filterStrategy = NoopFilteringStrategy.instance();
        else this.filterStrategy = FilteringStrategy.instance();
    }

    private ensureGroupIndicatorObserver(): void
    {
        if (this.groupIndicatorObserver)
        {
            return;
        }
        this.ngZone.runOutsideAngular(() =>
        {
            this.groupIndicatorObserver = new MutationObserver(() => this.wireGroupIndicatorAccessibility());
            this.groupIndicatorObserver.observe(this.grid.nativeElement, { childList: true, subtree: true });
        });
    }

    private addGroupIndicatorKeydownListener(element: HTMLElement): void
    {
        const handler = (event: KeyboardEvent): void =>
        {
            if (event.key === "Enter" || event.key === " ")
            {
                event.preventDefault();
                element.click();
                event.stopPropagation();
            }
        };
        element.addEventListener("keydown", handler);
        this.groupIndicatorListeners.push({ element, handler });
    }

    private wireGroupIndicatorAccessibility(): void
    {
        const expandCollapseAllButton = this.grid.nativeElement.querySelector(".igx-grid__header-indentation");
        if (expandCollapseAllButton && !expandCollapseAllButton.hasAttribute("tabindex"))
        {
            expandCollapseAllButton.setAttribute("tabindex", "0");
            expandCollapseAllButton.setAttribute("role", "button");
            expandCollapseAllButton.setAttribute("aria-label", "expand or collapse all groups");
            this.addGroupIndicatorKeydownListener(expandCollapseAllButton as HTMLElement);
        }

        const groupingIndicators = this.grid.nativeElement.querySelectorAll(".igx-grid__grouping-indicator");
        groupingIndicators.forEach(indicator =>
        {
            if (indicator.hasAttribute("tabindex"))
            {
                return;
            }
            indicator.setAttribute("tabindex", "0");
            indicator.setAttribute("role", "button");
            const rowIndexAttr = indicator.parentElement?.attributes["data-rowindex"];
            if (rowIndexAttr)
            {
                indicator.setAttribute("aria-label", `expand or collapse group at row ${parseInt(rowIndexAttr.value) + 1}`);
            }
            else
            {
                indicator.setAttribute("aria-label", "expand or collapse group");
            }
            this.addGroupIndicatorKeydownListener(indicator as HTMLElement);
        });
    }

    private removeGroupIndicatorListeners(): void
    {
        this.groupIndicatorListeners.forEach(({ element, handler }) => element.removeEventListener("keydown", handler));
        this.groupIndicatorListeners = [];
    }

    ngOnDestroy(): void
    {
        this.groupIndicatorObserver?.disconnect();
        this.removeGroupIndicatorListeners();
        if (this._prevRequest)
        {
            this._prevRequest.unsubscribe();
        }
        if (this.verticalScrollElement && this.verticalScrollListener)
            this.verticalScrollElement.removeEventListener("scroll", this.verticalScrollListener);
        this.remoteData.complete();
        this.destroy$.next();
        this.destroy$.complete();
        this.exitCellEdit();
        this.firstLayoutLoadFromCache = true;
        this.cacheLocalGridLayout(false);
        this.$keyHandlerService.unsubscribe(Key.A, ModifierKey.Alt);
        this.activeNodeChange(null);
        this.afterFetchCallback = null;
        this.summarySetup = {};
        this.grid.data = [];
    }

    doRepositoryFunction(command: string, row: RowCommandRow): void
    {
        if (this.isGridCellEdit() && command === "Edit")
        {
            this.$cellEditService.EditFirstCell(this.grid, this.viewMetadata(), this.grid.getRowByKey(row.id).index, true);
            return;
        }
        this.cacheLocalGridLayout(true);
        if (command === "EditMultiple") this.viewMetadata()["$multipleEdit"] = true;

        if (command === "Delete")
            this.viewMetadata()["$$Deleting"] = true;
        if (command === "Delete" || command === "Edit")
            this.clearSkipLayout = true;
        if (this.$metadataViewService.IsProjectionGrid(this.viewMetadata()) && command === "EditMultiple")
        {
            let editEntity: any;
            if (!this.viewMetadata().EditAction)
                editEntity = this.$multipleEntityEditService.EditableEntities[this.$multipleEntityEditService.CurrentEditIndex];
            else
                editEntity = this.$multipleEntityEditService.editActionEntity;
            if (editEntity)
            {
                const idProperty = this.viewMetadata()["GridProperties"].IdProperty;
                const entity = {};
                entity[idProperty] = editEntity[idProperty];
                this.datacontroller()["$$ProjectionGridEntity"] = entity;
            }
        }

        this.repositoryFunction.emit(<RowCommandArgs>{
            command: command,
            row: row,
        });
    }

    cacheLocalGridLayout(editClick: boolean): void
    {
        if ((!editClick && this.$multipleEntityEditService.ProviderId) || this.totalCount() === 0)
            return;
        this.saveGridState(true);
    }

    restoreLocalGridLayout(): void
    {
        if (this.$multipleEntityEditService.ProviderId === this.providerId) return;
        requestAnimationFrame(() => this.loadGridState(true));
    }

    resetScrollPosition(): void
    {
        this.$gridLayoutService.ScrollPosition[this.providerId] = "0";
    }

    //Keep igx virtualization in sync with the real scroll position after a fast scroll
    private syncVirtualScrollOnSettle(): void
    {
        const container = this.grid?.verticalScrollContainer;
        const scrollEl = container?.getScroll();
        if (Utilities.IsNullOrUndefined(scrollEl))
            return;

        this.ngZone.runOutsideAngular(() =>
        {
            fromEvent(scrollEl, "scroll")
                .pipe(debounceTime(80), takeUntil(this.destroy$))
                .subscribe(() =>
                {
                    if (container.scrollPosition === scrollEl.scrollTop)
                        return;
                    this.ngZone.run(() => container.onScroll({ target: scrollEl }));
                });
        });
    }

    handlePreLoadImpl(): void
    {
        this.$providerStorageService.Scrolling[this.providerId] = true;
        this.holdScrollPosition = true;
        this.fetching.set(true);
        this.$gridSelectionService.Selecting[this.providerId] = false;
        this.fetch();
    }

    fetch(initial: boolean = false, loadRecent: boolean = false, buildColumnSummaries: boolean = false, fromRecalculate: boolean = false, takeAll: boolean = false): void
    {
        if (initial)
            this.scrollStartIndex = 0;
        if (!this.$providerStorageService.Scrolling[this.providerId] && this.$providerStorageService.LocalEntitiesStore[this.providerId] && !this.entireGridLoaded)
            this.$providerStorageService.LocalEntitiesStore[this.providerId].forEach(x => x["$processed"] = false);
        this.$providerStorageService.BuildSummaries[this.providerId] = buildColumnSummaries;
        this.$providerStorageService.IsDataLoading[this.viewMetadata().GridProperties["$providerId"]] = true;
        this.$gridSelectionService.RowSelectRecalculate[this.providerId] = false;

        let skip: number = 0;
        let take: number = 25;

        if (takeAll)
            take = this.totalCount();
        else if (this.$providerStorageService.Scrolling[this.providerId])
        {
            if (this.$providerStorageService.GridFetchType[this.providerId] === GridFetchType.ServerStore)
                skip = this.$providerStorageService.GridData[this.providerId].length;
            else
                take = this.$providerStorageService.GridData[this.providerId].length + 25;
        }
        else
            take = 50;

        const applyPaging = true;
        const forExport = this.model()?.$forExport;
        if (this.model())
            this.model().$forExport = false;
        if (initial && this.viewMetadata())
            this.$gridSortService.InitialSortDone[this.viewMetadata().GridProperties["$providerId"]] = false;

        const scrollPosition = parseInt(this.$gridLayoutService.ScrollPosition[this.providerId] ?? "0");

        if (scrollPosition > 0)
            take = take + scrollPosition;

        this._prevRequest?.unsubscribe();
        this._prevRequest = this.$gridLoadService.getData(
            this.grid,
            this.model(),
            this.viewMetadata(),
            applyPaging && !forExport,
            _applyPaging => this.afterFetch(initial, loadRecent, fromRecalculate),
            forExport ? null : skip,
            forExport ? null : take,
            forExport ? null : this.skipSort,
            forExport ? null : loadRecent
        );
    }

    afterFetch(initial: boolean, loadRecent: boolean, fromRecalculate: boolean): void
    {
        const self = this;
        self.$providerStorageService.IsDataLoading[self.viewMetadata().GridProperties["$providerId"]] = false;
        const gridData = self.$providerStorageService.GridData[self.viewMetadata().GridProperties["$providerId"]];
        if (gridData && gridData.length > 0)
        {
            if (self.model())
            {
                const parent = self.model()["$isEntityWrapper"] ? self.model()["$value"] : self.model();
                for (const row of gridData)
                {
                    const childModel = row["$$model"];
                    if (Utilities.IsNullOrUndefined(childModel) || childModel["$$Parent"])
                    {
                        continue;
                    }
                    childModel["$$Parent"] = parent;
                }
            }

            const summaryColumns = this.$gridMetadataService.getSummaryColumns(
                this.viewMetadata().GridProperties.ClassMetadata
            );
            if (summaryColumns && summaryColumns.length !== 0)
            {
                this.$gridSummaryService
                    .getColumnSummaries(this.providerId, this.grid, this.viewMetadata(), this.metadataServerPaging(), summaryColumns, loadRecent, this.model())
                    .then(result =>
                    {
                        afterFetchResolve(result);
                    });
                return;
            }
        }

        afterFetchResolve();
        function afterFetchResolve(columnSummaries?: any): void
        {
            const columns = self.viewMetadata()["$columns"];
            self.setCellClasses();
            if (self.$providerStorageService.ClearFilter[self.providerId])
            {
                self.clearFilter();
                self.$providerStorageService.ClearFilter[self.providerId] = false;
                // trigger reload of default layout after filter clear
                self.checkForLayout = true;
                self.layoutLoaded = false;
            }
            self.$providerStorageService.ApplyFilter[self.providerId] = false;

            if (!self.columns() || self.columns().length === 0 || (columns && columns.length !== self.columns().length) || self.$providerStorageService.ReloadColumns[self.providerId])
            {
                self.columns.set(columns);
                self.columns()?.forEach(c =>
                {
                    c.$renderInfo = self.getRenderInfo(c);
                    c.$computedDataType = self.getColumnDataType(c);
                    c.$cellId = `${self.providerId}_${c.Name}`;
                    if (c.DataType === "dateTime")
                        c.$dateTimeFormat = self.timeFormat(c.Name, true);
                    else if (c.DataType === "time")
                        c.$timeFormat = self.timeFormat(c.Name);
                    else if (c.DataType === "time24")
                        c.$time24Format = self.timeFormat24(c.Name);
                });
                self.$providerStorageService.ReloadColumns[self.providerId] = false;
            }
            else if (columns && columns.length !== 0)
                self.columns().forEach(column =>
                {
                    const col = columns.find(x => x.Name === column.Name);
                    if (col)
                    {
                        column.Label = col.Label;
                        column.Visible = col.Visible;
                        column.AlwaysHidden = col.AlwaysHidden;
                    }
                });
            const data = self.$providerStorageService.GridData[self.providerId];
            self.setColumnSummaries(columnSummaries, data);
            self.remoteData.next(Array.isArray(data) ? [...data] : data);

            self.initialized.set(true);
            self.hasCommands.set(!Utilities.IsNullOrUndefined(self.viewMetadata().Actions) && self.viewMetadata().Actions.length > 0);
            self.allowSort.set(!self.viewMetadata().GridProperties.DisableHeaderSorting);
            self.canAdd.set(self.viewMetadata().GridProperties.CanAdd);

            if (self.$cellEditService.CellEditCell[self.providerId] && data.length > 0 && data.filter(r => r.Id === self.$cellEditService.CellEditCell[self.providerId].id.rowID).length === 0)
                self.exitCellEdit();

            if (Utilities.IsNullOrUndefined(data))
            {
                self.fetching.set(false);
                self.grid.markForCheck();
                return;
            }

            self.totalCount.set(self.$providerStorageService.TotalHits[self.providerId]);
            self.setSelectionMode();
            self.$gridSelectionService.SetSelectDisabled(self.providerId, self.viewMetadata());
            self.registerCommands();
            self.fetching.set(false);
            let forceDeselect = false;
            self.refreshAction.set(true);

            if (self.checkForLayout && !self.layoutLoaded && self.totalCount() > 0 && !self.$providerStorageService.SkipLayout[self.providerId] && initial && !self.$multipleEntityEditService.MultipleEditLoading)
            {
                self.initializeLayout()
                    .then((layout) =>
                    {
                        requestAnimationFrame(() =>
                        {
                            self.layoutLoaded = true;
                            if (layout)
                            {
                                if (!layout.localRestored)
                                {
                                    self.$gridLayoutService.SetState(self.grid, self.state, layout, self.columns());
                                    self.setFilterDisplay();
                                }
                            }
                            else
                            {
                                self.restoreLocalGridLayout();
                            }

                            self.pinDefaultColumns();
                            if (!self.shouldEditAfterAdd())
                                self.isAdding.set(false);
                        });
                    })
                    .finally(() => self.checkForLayout = false);
            }

            if (initial)
            {
                if (!self.shouldEditAfterAdd())
                    self.isAdding.set(false);
                self.$providerStorageService.SkipLayout[self.providerId] = undefined;
                self.$gridSelectionService.AllSelected[self.providerId] = false;
                if (self.viewMetadata()["$multipleDelete"] || (self.viewMetadata()["$multipleEdit"] && !self.$multipleEntityEditService.ProviderId))
                {
                    delete self.viewMetadata()["$multipleEdit"];
                    delete self.viewMetadata()["$multipleDelete"];
                    self.grid.deselectAllRows(false);
                    self.$gridSelectionService.AllDeselected[self.providerId] = true;
                    forceDeselect = true;
                }
                else self.$gridSelectionService.AllDeselected[self.providerId] = false;
            }

            if (self.clearSkipLayout)
            {
                self.$providerStorageService.SkipLayout[self.providerId] = undefined;
                self.clearSkipLayout = false;
            }

            requestAnimationFrame(() =>
            {
                if (!self.$providerStorageService.Scrolling[self.providerId])
                    self.setGridHeight();
                self.setColumnWidths();
                self.MaybeRunRecalculate();
                self.applySummaries();

                for (const summaryField in columnSummaries)
                {
                    const gridColumn = self.grid.columns.find(c => c.field === summaryField);
                    if (gridColumn)
                        gridColumn.pipeArgs = <IColumnPipeArgs>{ digitsInfo: self.columns().find(x => x.Name === summaryField).DigitsInfo };
                }
                self.$gridEntityService.fireModelChangedListeners(self.providerId);

                if (self.isGroupedGrid)
                {
                    self.wireGroupIndicatorAccessibility();
                    self.ensureGroupIndicatorObserver();
                }
            });
            self.$gridSelectionService.SetSelected(
                self.providerId,
                self.grid,
                initial,
                self.viewMetadata(),
                self.$gridSelectionService.AllSelected[self.providerId],
                self.$gridSelectionService.AllDeselected[self.providerId],
                forceDeselect,
                fromRecalculate
            );

            self.hasSelection.set(Utilities.IsNullOrUndefined(self.grid) ? false : self.$gridSelectionService.HasSelection(self.providerId));

            if (self.cellEditNextEditableEnabledChange)
            {
                self.cellEditNextEditableEnabledChange = false;
                self.$cellEditService.ActivateEditableCell(self.grid, self.$cellEditService.GetNextEditableCellSafe(self.grid, self.viewMetadata(), self.currentColumnIndex, self.currentRowIndex, self.grid.columns[self.currentColumnIndex].visibleIndex));
            }

            self.grid.markForCheck();
            self.removeHiddenSorts();

            if (initial)
            {
                if (self.canAdd() && !self.isViewOnly())
                {
                    self.$keyHandlerService.subscribe(self.constructor.name, Key.A, ModifierKey.Alt, "This Screen|Add item to grid", () =>
                    {
                        if (!self.isAdding())
                            self.addItem(true);
                    });
                }
            }

            const scrollPosition = self.$providerStorageService.LastScrollPosition[self.providerId] ?? 0;

            requestAnimationFrame(() =>
            {
                if (self.shouldRestoreScrollAfterFetch())
                {
                    if (scrollPosition > 0)
                    {
                        self.grid.verticalScrollContainer.scrollPosition = scrollPosition;
                    }
                    else
                        self.grid.navigateTo(0, initial ? 0 : -1, args =>
                        {
                            args.target.nativeElement.focus();
                            requestAnimationFrame(() => args.target.grid.verticalScroll.scrollPosition = scrollPosition);
                        });

                    setTimeout(() =>
                    {
                        if(self.$providerStorageService.LastEditedRecordIndex[self.providerId] > -1)
                        {
                            self.$providerStorageService.LastEditedRecordIndex[self.providerId] = -1;
                            self.grid.markForCheck();
                        }
                    }, 2500);
                }

                self.holdScrollPosition = false;
                self.showToolbar.set(true);
                self.$providerStorageService.Scrolling[self.providerId] = false;
            });

            self.cdr.detectChanges();

            if (scrollPosition === 0 && initial && self.$providerStorageService.GridData[self.providerId].length < self.totalCount() && !self.$multipleEntityEditService.MultipleEditLoading)
                requestAnimationFrame(() =>
                {
                    if (self.grid && !self.grid.hasVerticalScroll())
                        self.handlePreLoadImpl();
                });

            if (self.afterFetchCallback)
            {
                const callback = self.afterFetchCallback;
                self.afterFetchCallback = null;
                callback();
            }

            if (self.shouldResumeEditAfterAdd())
                self.cellEditAfterAdd();
        }
    }

    setColumnSummaries(columnSummaries: any, data: any): void
    {
        for (const summaryField in columnSummaries)
        {
            const summary = this.$gridFormatService.summaryFormatter(
                columnSummaries[summaryField],
                this.$gridMetadataService.getProperty(this.viewMetadata()?.GridProperties.ClassMetadata, summaryField)?.Scale
            );

            data?.forEach(row =>
            {
                row[`total.${summaryField}`] = summary;
                row[`average.${summaryField}`] = summary.replace("$", "").replace(/,/g, "") / this.$providerStorageService.TotalHits[this.providerId];
            });
        }
    }

    pinDefaultColumns(): void
    {
        this.columns().filter(x => x.DefaultPinned)
            .forEach(c =>
            {
                const column = this.grid.columns.find(x => x.field === c.Name);
                if (column)
                    column.pinned = true;
            });
    }

    removeHiddenSorts(): void
    {
        if (!this.columns() || !this.grid.sortingExpressions)
            return;

        for (let i = this.grid.sortingExpressions.length - 1, l = 0; i >= l; i--)
        {
            const expression = this.grid.sortingExpressions[i];
            const sortedColumn = this.columns().find(x => !x.AlwaysHidden && x.Name === expression.fieldName);
            if (!sortedColumn)
                this.grid.sortingExpressions.splice(this.grid.sortingExpressions.indexOf(expression), 1);
        }
    }

    readonly groupSortByCount = signal(false);

    toggleGroupSortMode(): void
    {
        this.groupSortByCount.set(!this.groupSortByCount());
        this.applyGroupSortStrategy(this.grid.groupingExpressions);
        this.grid.pipeTrigger++;
        this.grid.notifyChanges();
    }

    private applyGroupSortStrategy(expressions: IGroupingExpression[]): void
    {
        expressions.forEach(expression => expression.strategy = this.groupSortByCount()
            ? GroupedGridSortingStrategy.instance()
            : DefaultSortingStrategy.instance());
    }

    get isGroupedGrid(): boolean
    {
        return !!this.viewMetadata() && this.viewMetadata().Groupable && this.$providerStorageService.GridFetchType[this.providerId] !== GridFetchType.ServerStore;
    }

    isColumnGrouped(field: string): boolean
    {
        return this.grid.groupingExpressions?.some(e => e.fieldName === field) ?? false;
    }

    toggleColumnGrouping(field: string, event: Event): void
    {
        event.preventDefault();
        event.stopPropagation();
        if (this.isColumnGrouped(field))
        {
            this.grid.clearGrouping(field);
            return;
        }
        const column = this.grid.getColumnByName(field);
        this.grid.groupBy(<IGroupingExpression>{
            fieldName: field,
            dir: this.grid.sortingExpressions.find(e => e.fieldName === field)?.dir ?? SortingDirection.Asc,
            ignoreCase: column.sortingIgnoreCase,
            strategy: column.sortStrategy,
            groupingComparer: column.groupingComparer
        });
    }

    get enableLayout(): boolean
    {
        return this.$gridLayoutService.EnableLayout[this.providerId];
    }

    shouldLoadCachedLayout(): boolean
    {
        const cachedState: IGridState = <IGridState>JSON.parse(this.$gridLayoutService.LocalCachedLayout[this.providerId].split("|")[0]);
        let shouldLoad: boolean = true;
        this.viewMetadata()["$columns"].forEach(c =>
        {
            if (shouldLoad)
            {
                const column = cachedState.columns.find(x => x.field === c.Name);
                if (column)
                {
                    //column became hidden outside of grid
                    if ((!c.Visible && !column.hidden) || (c.Visible && column.hidden))
                        shouldLoad = false;
                    //column editability changed
                    // this check doesn't work as cachedState doesn't appear to be reliably synchronized with AllowCellEdit
                    // if (this.$cellEditService.AllowCellEdit(this.grid, this.viewMetadata(), this.currentColumnIndex, c.Name) !== column.editable)
                    //     shouldLoad = false;
                }
            }

        });
        if (!shouldLoad && !this.$multipleEntityEditService.ProviderId)
            this.updateLayout(this.$gridLayoutService.LocalCachedLayout[this.providerId].split("|")[0]);
        return shouldLoad;
    }

    initializeLayout(): Promise<any>
    {
        const self = this;
        if (this.$gridLayoutService.HasLocalCachedLayout(this.providerId) && this.shouldLoadCachedLayout())
        {
            this.restoreLocalGridLayout();
            return Promise.resolve({localRestored: true});
        }
        else
            if (this.viewMetadata().DisableLayout)
                return Promise.resolve(null);
        return this.$gridLayoutService.LayoutEnabled(this.providerId)
            .then((enabled: boolean) =>
            {
                if (enabled)
                    return self.$gridLayoutService.FetchDefaultLayout(self.providerId)
                        .then(layout =>
                        {
                            if (layout)
                            {
                                const parsed = layout.split("|");
                                layout = parsed[0];
                            }
                            return layout;
                        });
            });
    }

    getColumnWidth(columnName: string): string
    {
        return this.initialWidths[columnName];
    }

    setColumnWidths(): void
    {
        this.grid.columns.forEach(c => this.setColumnWidth(c.field));
    }

    totalDefinedWidths: number = 0;

    setColumnWidth(columnName: string): void
    {
        const column = this.grid.columns.find(x => x.field === columnName);
        if (!column)
        {
            this.initialWidths[columnName] = null;
            return;
        }
        let width = this.$gridMetadataService.getProperty(
            this.viewMetadata()?.GridProperties?.ClassMetadata,
            column.field
        )?.DrawWidth;
        if (width === -1)
            width = 100;
        if (this.layoutLoaded || !width)
        {
            this.initialWidths[columnName] = column.width;
            return;
        }
        if (width === 100 && this.grid.columns.length > 1)
            this.initialWidths[columnName] = null;
        else
        {
            this.totalDefinedWidths += width;
            if (this.totalDefinedWidths === 100)
            {
                this.initialWidths[columnName] = null;
                this.totalDefinedWidths = 0;
            }
            else
                this.initialWidths[columnName] = `${width}%`;
        }

    }

    get title(): string
    {
        return this.viewMetadata()?.Title;
    }

    get singleCommand(): boolean
    {
        return this.hasCommands() && this.viewMetadata().Actions.filter(x => !x.Hidden).length === 1;
    }

    get commandMenu(): boolean
    {
        return this.hasCommands() && !this.singleCommand;
    }

    get commandBar(): boolean
    {
        return this.singleCommand;
    }

    get hasMultipleSelected(): boolean
    {
        if (Utilities.IsNullOrUndefined(this.grid) || !this.showDeleteMultiple)
            return false;
        return this.$gridSelectionService.SelectedCount[this.providerId] > 1;
    }

    get cellSelectionMode(): GridSelectionMode
    {
        return GridSelectionMode.single;
    }

    get cellEditModelParent(): any
    {
        return this.cellEditModel()?.$$Parent;
    }

    deleteMultipleRows(): void
    {
        if (!this.allowDeleteMultiple())
        {
            this.$commonDialogsService.showAlertMessage("You are not allowed to delete the selected items.", "Delete items");
            return;
        }

        const selectedCount = this.$gridSelectionService.SelectedCount[this.providerId];
        const msg = `Are you sure you want to delete these ${selectedCount} items?`;

        this.$commonDialogsService.showConfirmationMessage(msg, `Delete ${selectedCount} items`)
            .then(() =>
            {
                if (this.useRepository())
                {
                    this.isMassDeleting.set(true);

                    let models: any[];
                    if (this.$providerStorageService.LocalEntitiesStore[this.providerId])
                        models = this.$providerStorageService.LocalEntitiesStore[this.providerId];
                    else
                        models = this.$providerStorageService.GridData[this.providerId].map(x => x["$$model"]);
                    const deleteModels = models.filter(x => this.$gridSelectionService.SelectedIds[this.providerId].indexOf(ObjectHelpers.GetId(this.viewMetadata().GridProperties.IdProperty, x, 0)) > -1);
                    this.viewMetadata()["$multipleDelete"] = true;

                    deleteModels.forEach(model =>
                    {
                        const id = ObjectHelpers.GetId(this.viewMetadata().GridProperties.IdProperty, model, 0);
                        const row = this.$providerStorageService.GridData[this.providerId].find(x => x.Id === id);
                        if (row)
                            row["$$Deleting"] = true;

                    });
                    this.$gridDeleteService.BulkDelete(deleteModels, this.datacontroller(), this.model(), this.viewMetadata())
                        .then(result =>
                        {
                            if (result.length > 0)
                            {
                                //these are retries, which all either have a valid exception, ASC or a VQH requiring a user response
                                this.isDeleteRetrying = true;
                                this.modelsToDelete = result;
                                this.indexToDelete = 0;
                                this.$gridDeleteService.triggerDeleteRetry();
                            }
                            else
                            {
                                this.isMassDeleting.set(false);
                            }
                        })
                        .finally(() =>
                        {
                            this.isMassDeleting.set(false);
                        });
                }
            });
    }

    processNextDelete(): void
    {
        if (!this.isDeleteRetrying)
            return;
        if (this.modelsToDelete.length <= this.indexToDelete)
        {
            this.$providerStorageService.SkipLayout[this.providerId] = true;
            this.$gridEventService.fireLoadMetadataTableListeners(<MetadataTableEventArg>{
                Type: this.viewMetadata().GridProperties.ClassMetadata.TypeName,
                AllSubPropertyGrids: false
            });

            this.viewMetadata()["$$Deleting"] = false;
            this.$gridSelectionService.SetSelectedIds(this.providerId, []);
            this.isDeleteRetrying = false;
            this.modelsToDelete = [];
            this.indexToDelete = 0;

            this.isMassDeleting.set(false);
            return;
        }

        const model = this.modelsToDelete[this.indexToDelete];
        const id = ObjectHelpers.GetId(this.viewMetadata().GridProperties.IdProperty, model, 0);
        const row = this.$providerStorageService.GridData[this.providerId].find(x => x.Id === id);
        if (row)
            row["$$Deleting"] = true;

        this.repositoryFunction.emit(<RowCommandArgs>{
            command: "Delete",
            row: <RowCommandRow>{
                id: id,
                model: model
            }
        });
        this.indexToDelete++;
    }

    get showDeleteMultiple(): boolean
    {
        if (Utilities.IsNullOrUndefined(this.grid))
            return false;
        if (this.viewMetadata()?.SelectionMode !== SelectionMode.Multiple)
            return false;
        if (this.totalCount() < 1)
            return false;
        if (!this.$gridSelectionService.SelectedIds[this.providerId] || this.$gridSelectionService.SelectedIds[this.providerId].length === 0)
            return false;
        if (!this.viewMetadata().GridProperties.CanDelete)
            return false;
        return true;
    }

    allowDeleteMultiple(): boolean
    {
        let canDelete = true;

        let models: any[];
        if (this.$providerStorageService.LocalEntitiesStore[this.providerId])
            models = this.$providerStorageService.LocalEntitiesStore[this.providerId];
        else
            models = this.$providerStorageService.GridData[this.providerId]?.map(x => x["$$model"]);
        if (!models || models.length === 0)
            return false;
        const selectedModels = models?.filter(x => this.$gridSelectionService.SelectedIds[this.providerId].indexOf(ObjectHelpers.GetId(this.viewMetadata().GridProperties.IdProperty, x, 0)) > -1);
        if (selectedModels.length < this.$gridSelectionService.SelectedIds[this.providerId].length)
            return false;
        let i = 0;
        while (canDelete && i < selectedModels.length)
        {
            if (!this.$gridMetadataService.canDo("Delete", selectedModels[i], this.isViewOnly(), this.viewMetadata()))
                canDelete = false;
            i++;
        }
        return canDelete;
    }

    get AllowMultipleDelete(): boolean
    {
        //Maybe support this at some point but currently, each row will be guaranteed to have to make separate recalc calls
        return !this.viewMetadata()?.AllowDeleteCheckProperty && this.hasMultipleSelected;
    }

    filterable(column: any): boolean
    {
        return column.IsFilterable && !this.fetching() && (!column.LookupType || !this.lookupLoading(column));
    }

    filterDisplay: string;

    setFilterDisplay(): void
    {
        if (this.viewMetadata().GridProperties?.ClassMetadata)
            this.filterDisplay = this.$gridFilterService.parseGridFilter(this.grid?.filteringExpressionsTree, this.viewMetadata().GridProperties.ClassMetadata);
    }

    get AutoAddNewRow(): boolean
    {
        if (Utilities.IsNullOrUndefined(this.viewMetadata().GridProperties?.AutoAddNewRow))
            return true;
        return this.viewMetadata().GridProperties.AutoAddNewRow;
    }

    get hasFilter(): boolean
    {
        return Utilities.IsNullOrUndefined(this.grid?.filteredData) ? false : this.grid.filteredData.length > 0;
    }

    get showFiltered(): boolean
    {
        //Different from hasFilter() because we want to show this even if the count is zero
        return !Utilities.IsNullOrUndefined(this.grid?.filteredData);
    }

    get hiddenSelections(): boolean
    {
        if (!this.showFiltered || this.$gridSelectionService.SelectedCount[this.providerId] < 1)
            return false;
        const filteredIds: Set<string> = new Set(this.grid.filteredData.map(x => x.Id));
        return this.$gridSelectionService.SelectedIds[this.providerId]?.some(id => !filteredIds.has(id));
    }

    shouldEditAfterAdd(): boolean
    {
        return this.cellAdding || this.$cellEditService.CellEditAfterAdd[this.providerId];
    }

    //editFirstCellAfterAdd: cellEditAfterAdd fetched the remaining rows before it could reach the new one and is waiting on this fetch
    shouldResumeEditAfterAdd(): boolean
    {
        return this.shouldEditAfterAdd() || this.editFirstCellAfterAdd;
    }

    shouldRestoreScrollAfterFetch(): boolean
    {
        return !this.cellEditing && !this.$gridSelectionService.Selecting[this.providerId] && !this.holdScrollPosition && !this.activatingCellEdit;
    }

    showIdTemplate(): boolean
    {
        return Utilities.IsNullOrUndefined(this.columns()?.find(x => x.Name === "Id"));
    }

    columnHidden(name: string): boolean
    {
        return !this.columns().find(x => x.Name === name)?.Visible;
    }

    setupSummaryColumn(name: string): void
    {
        if (!this.$providerStorageService.GridData[this.providerId] || this.$providerStorageService.GridData[this.providerId].length === 0)
            return;
        const property = this.$gridMetadataService.getProperty(this.viewMetadata().GridProperties.ClassMetadata, name);
        if (Utilities.IsNullOrUndefined(property))
            return;
        this.summarySetup[name] =
        {
            HasSummary: property.SummaryType === MetadataSummary.Sum || property.SummaryType === MetadataSummary.Average,
            SummaryType: property.SummaryType
        };
    }

    applySummaries(): void
    {
        if (!this.columns())
            return;
        this.grid.columns.forEach(gridColumn =>
        {
            const column = this.columns().find(c => c.Name === gridColumn.field);
            if (!column)
                return;
            this.setupSummaryColumn(column.Name);
            const hasSummary = this.hasSummary(column.Name);
            gridColumn.hasSummary = hasSummary;
            gridColumn.summaries = hasSummary ? this.useSummary(column.IsCurrency, column.Name) : undefined;
        });
    }

    useSummary(isCurrency: boolean, name: string): typeof IgxNumberSummaryOperand
    {
        if (!this.summarySetup[name])
            this.setupSummaryColumn(name);
        if (!this.summarySetup[name])
            return null;
        if (this.summarySetup[name].SummaryType === MetadataSummary.Average)
        {
            if (isCurrency)
                return CurrencyAverage;
            return Average;
        }
        if (isCurrency)
            return CurrencySummary;
        return Summary;
    }

    hasSummary(name: string): boolean
    {
        if (!this.summarySetup[name])
            this.setupSummaryColumn(name);
        if (!this.summarySetup[name])
            return false;
        return this.summarySetup[name].HasSummary;
    }

    registerCommands(): void
    {
        if (!this.hasCommands())
            return;
        this.commands = [];
        this.viewMetadata().Actions.forEach(cmd =>
        {
            const label = this.$metadataService.EvaluateString(
                cmd.Label, this.datacontroller()?.Entity?.["$$Parent"], this.datacontroller()?.Entity);

            this.commands.push(
                new LabelCommand({
                    label,
                    tooltip: label,
                    action: $event => this.performCommand(cmd, $event),
                    getEnabled: () => !this.$metadataService.GetPermissionDenied(cmd.Disabled, cmd.DisabledProperty, this.model(), this.datacontroller()) && this.hasSelection(),
                    getVisible: () => !this.$metadataService.GetPermissionDenied(cmd.Hidden, cmd.HiddenProperty, this.model(), this.datacontroller())
                }));
        });
    }

    performCommand(action: IMetadataAction, $event?: Event): void
    {
        const self = this;
        if ($event !== undefined)
        {
            $event.stopPropagation();
        }

        const rows = this.$providerStorageService.GridData[this.providerId].filter(x => self.$gridSelectionService.SelectedIds[self.providerId].indexOf(x.Id) > -1);
        const metadataAction = Utilities.deepCopy(action);
        metadataAction.Context = null;

        let modelToUse: any;
        if (MetadataActionHelpers.isNoServicePath(action) && this.viewMetadata().SubPropertyName)
            modelToUse = this.model();
        else
            modelToUse = rows.map(x => x.Id);

        const repositoryForm = Utilities.IsNullOrUndefined(this.datacontroller())
            ? new RepositoryFormData(null, null)
            : this.datacontroller();
        this.$metadataActionService
            .ExecuteAction(
                modelToUse,
                metadataAction,
                repositoryForm
            )
            .then(_x =>
            {
                self.grid.selectionService.rowSelection.clear();
                self.$providerStorageService.GridData[self.providerId].forEach(x => (x.Selected = false));
                self.$gridSelectionService.SetSelectedIds(self.providerId, []);
            });
    }

    get hideRowSelectors(): boolean
    {
        return (
            this.viewMetadata()?.SelectionMode !== SelectionMode.Multiple &&
            this.viewMetadata()?.SelectionMode !== SelectionMode.Single
        );
    }

    setSelectionMode(): void
    {
        if (!this.initialized() || Utilities.IsNullOrUndefined(this.grid))
            return;
        const enable = (this.hasCommands() && this.viewMetadata().Actions.filter(x => !x.Hidden).length > 0) || this.viewMetadata()["$selectOnly"];
        if (enable)
            this.selectionMode.set(this.getRowSelectionMode());
        else if (this.viewMetadata().EditAction || this.viewMetadata().EditForm)
            this.selectionMode.set("multiple");
        else
            this.selectionMode.set("none");
    }

    getRowSelectionMode(): GridSelectionMode
    {
        if (!Utilities.IsNullOrUndefinedOrEmpty(this.viewMetadata().AllowSelectionProperty))
        {
            let model = this.model();
            if (this.viewMetadata().SubPropertyType && this.datacontroller().OtherEntities && this.datacontroller().OtherEntities[this.viewMetadata().SubPropertyType] && !this.viewMetadata().AllowSelectionProperty.includes("Parent"))
                model = this.datacontroller().OtherEntities[this.viewMetadata().SubPropertyType][0];

            if (this.$metadataService.EvaluateBoolean(this.viewMetadata().AllowSelectionProperty, model["$$Parent"], model))
                return this.viewMetadata()?.SelectionMode === SelectionMode.Single ? "single" : "multiple";
            else
                return "none";
        }
        return this.viewMetadata()?.SelectionMode === SelectionMode.Single ? "single" : "multiple";
    }

    addItem(leaveCellEdit: boolean): void
    {
        if (this.isGridCellEdit() && this.hasFilter)
            this.addBooleanFilter("$cellEditAdded", true);

        this.isAdding.set(true);
        if (leaveCellEdit && this.cellEditing)
            this.leaveCellEdit(false);

        const self = this;
        requestAnimationFrame(() =>
        {
            self.fetching.set(true);
            self.cellEditing = false;
            if (self.useRepository())
                self.repositoryFunction.emit(<RowCommandArgs>{ command: "Add" });
            self.fetching.set(false);
        });
    }

    doAction(cell: CellType): boolean
    {
        const self = this;
        const row = cell.row.data;
        const column = self.columns().find(x => x.Name === cell.column.field);
        if (!column)
            return;
        if (this.isDisabled(cell) || this.isHidden(cell)) return;
        let actionToExecute: IMetadataAction;
        if (!Utilities.IsNullOrUndefined(column.MetadataAction)) actionToExecute = column.MetadataAction;
        else if (!Utilities.IsNullOrUndefined(column.LinkAction)) actionToExecute = column.LinkAction;
        else return false;
        this.$gridEntityService.fetchRowModel(row, this.datacontroller(), this.viewMetadata())
            .then(model =>
            {
                row["$$model"] = model;
                self.$gridEntityService.fireModelChangedListeners(self.providerId);
                self.$gridActionService.PerformMetadataAction(
                    self.providerId,
                    row,
                    actionToExecute,
                    self.datacontroller(),
                    self.viewMetadata(),
                    self.repositoryFunction
                );
            });
    }

    getDataController(action: IMetadataAction, type: string): RepositoryFormData
    {
        if (Utilities.IsNullOrUndefined(action)) return new RepositoryFormData(null, null);
        if (!Utilities.IsNullOrUndefined(this.datacontroller())) return this.datacontroller();
        if (!Utilities.IsNullOrUndefined(action.ViewToShow))
            type = this.$metadataViewService.GetTypeName(action.ViewToShow);
        return new RepositoryFormData(type, null);
    }

    isSelectionAllowed(id: string): boolean
    {
        return this.$gridSelectionService.IsSelectionAllowed(this.providerId, id);
    }

    rowCheckSelect(): void
    {
        this.$gridSelectionService.RowSelect[this.providerId] = true;
    }

    /*
        TODO: MJ - igx v15 changed the output paramters for the row selection event. This works for now,
        but we may want to overhaul all the downstream code to account for the new way of selection
    */
    translateEvent(event: any): IRowSelectionEventArgs
    {
        const newEvent = {
            added: [],
            allRowsSelected: event.allRowsSelected,
            cancel: event.cancel,
            event: event.event,
            newSelection: [],
            oldSelection: [],
            owner: event.owner,
            removed: []
        };

        if (!Utilities.IsNullOrUndefinedOrEmpty(event.added))
            newEvent.added = event.added.map(x => x.Id);
        if (!Utilities.IsNullOrUndefinedOrEmpty(event.removed))
            newEvent.removed = event.removed.map(x => x.Id);
        if (!Utilities.IsNullOrUndefinedOrEmpty(event.oldSelection))
            newEvent.oldSelection = event.oldSelection.map(x => x.Id);
        if (!Utilities.IsNullOrUndefinedOrEmpty(event.newSelection))
            newEvent.newSelection = event.newSelection.map(x => x.Id);
        return newEvent;
    }

    public onCellSelect(event: IGridCellEventArgs): void
    {
        this.clearRowFocus();
        const row = this.grid.nativeElement.querySelector(`igx-grid-row[data-rowindex='${event.cell.row.index}'] igx-display-container`);
        if (row)
        {
            row.classList.add("row-focused");
        }
    }

    private clearRowFocus(): void
    {
        const elements = document.querySelectorAll('.row-focused');
        elements.forEach(element =>
        {
            element.classList.remove('row-focused');
        });
    }

    public onRowSelect(event: IRowSelectionEventArgs): void
    {
        const newEvent = this.translateEvent(event);
        if (!this.validSelection(newEvent))
        {
            event.cancel = true;
            return;
        }
        if (!this.$gridSelectionService.RowSelect[this.providerId] && this.clickedInCell(newEvent))
        {
            let changedId: string;
            if (newEvent.added.length === 1)
                changedId = newEvent.added[0];
            else if (newEvent.removed.length === 1)
                changedId = newEvent.removed[0];
            if (changedId && !this.isSelectionAllowed(changedId))
            {
                event.cancel = true;
                return;
            }
            this.rowCheckSelect();
        }
        const self = this;
        const doOnSelected = (args: TableRowSelectArgs): void =>
        {
            self.doOnSelected(args);
        };
        this.$gridSelectionService.OnRowSelect(this.providerId, newEvent, this.viewMetadata(), this.grid, doOnSelected);
        this.hasSelection.set(Utilities.IsNullOrUndefined(this.grid) ? false : this.$gridSelectionService.HasSelection(this.providerId));
    }

    validSelection(event: any): boolean
    {
        if (event.event.target.classList.contains("cell-edit"))
            event.cancel = true;
        if (event.event.target.parentElement.classList.contains("cell-edit"))
            event.cancel = true;
        if (event.event.target.parentElement.parentElement?.classList.contains("cell-action"))
            event.cancel = true;
        if (this.viewMetadata()?.SelectionMode === SelectionMode.None)
            event.cancel = true;
        if (event.event.target.nodeName.toLowerCase() === "igx-grid-row" || event.event.target.nodeName.toLowerCase() === "igx-grid-cell" || (typeof event.event.target.className === "string" && event.event.target.className.toLowerCase() === "igx-grid-cell-value"))
            event.cancel = true;
        if (!Utilities.IsNullOrUndefined(event.event.target.closest("row-actions")))
            event.cancel = true;
        if (event.event.target.className === "grid-icon" || event.event.target.querySelector(".grid-icon"))
            event.cancel = true;
        if (event.event.target.parentElement.nodeName.toLowerCase() === "forge-checkbox"
            || event.event.target.nodeName.toLowerCase() === "forge-checkbox"
            || event.event.target.parentElement.nodeName.toLowerCase() === "metadata-input-checkbox"
            || event.event.target.nodeName.toLowerCase() === "metadata-input-checkbox")
            event.cancel = true;
        if (event.event.target.nodeName.toLowerCase() === "forge-icon")
            event.cancel = true;
        return !event.cancel;
    }

    clickedInCell(event: any): boolean
    {
        if (event.event.target.className === "row-selector" || event.event.target.querySelector(".row-selector"))
            return true;
        return false;
    }

    doOnSelected(args: TableRowSelectArgs): void
    {
        if (!Utilities.IsNullOrUndefined(this.onSelected))
            this.onSelected.emit(args);
    }

    cellActionClass(column: any, cell: CellType): boolean
    {
        if (column.IsCellAction)
        {
            const state = this.$gridCellStateService.getCellState(cell);
            return !state?.isDisabled;
        }
        return false;
    }

    getIcon(cell: CellType): string
    {
        const state = this.$gridCellStateService.getCellState(cell);
        return state?.getIcon;
    }

    useIconSpan(cell: CellType): boolean
    {
        const state = this.$gridCellStateService.getCellState(cell);
        return state?.useIconSpan;
    }

    isHidden(cell: CellType): boolean
    {
        const state = this.$gridCellStateService.getCellState(cell);
        return state?.isHidden;
    }

    isDisabled(cell: CellType): boolean
    {
        const state = this.$gridCellStateService.getCellState(cell);
        return state?.isDisabled || this.isDisabledAction(cell);
    }

    isDisabledAction(cell: CellType): boolean
    {
        if (!this.recalculating())
            return false;
        const column = this.columns().find(x => x.Name === cell.column.field);
        if (!column)
            return false;
        return !Utilities.IsNullOrUndefinedOrEmpty(column.MetadataAction);
    }

    get allowCsvExport(): boolean
    {
        return Utilities.IsNullOrUndefinedOrEmpty(this.grid.groupingExpressions);
    }

    exportData(type: string): void
    {
        this.toolbar.showProgress = true;

        let exportService;
        if (type === "excel")
            exportService = this.excelExportService;
        else
            exportService = this.csvExportService;

        const destroyExportPipe$ = new Subject<void>();
        const selectedRowIds = new Set(this.selectionMode() === "none" ? [] : this.grid.selectedRows);
        let exportColumns: IExportColumnConfiguration;
        let cellFormatColumns = this.viewMetadata()["$columns"].filter(x => x.DataType === "percent" || x.DataType === "boolean");

        exportService.columnExporting.pipe(takeUntil(destroyExportPipe$)).subscribe((columnsArgs: IColumnExportingEventArgs) =>
        {
            // the exporters are application wide, so this can be another grid exporting
            if (!exportColumns)
                return;

            if (exportColumns.hiddenColumnFields.includes(columnsArgs.field))
            {
                columnsArgs.cancel = true;
                return;
            }

            if (exportColumns.handleLevelColumn(columnsArgs))
                return;

            if (columnsArgs.field === exportColumns.groupCaptionColumn?.field)
            {
                columnsArgs.header = exportColumns.captionHeader;
                return;
            }

            const column = this.viewMetadata()["$columns"].find(x => x.Name === columnsArgs.field);
            //numbers will export as numbers with no scale formatting
            if (column?.DataType === "number" || column?.DataType === "nonDelineatedNumber")
                columnsArgs.skipFormatter = true;
            //TODO: numbers will export with scale formatting if it is there, but formatted as string. igx has no solution to accommodate both
            // if (!column?.DigitsInfo)
            //     columnsArgs.skipFormatter = true;

            const header = column?.Label.replace(/&/g, "&amp;");
            if (header)
                columnsArgs.header = header;
        });

        exportService.rowExporting.pipe(takeUntil(destroyExportPipe$)).subscribe((args: IRowExportingEventArgs) =>
        {
            cellFormatColumns.forEach(column =>
            {
                const value = args.rowData[column.Name];
                if (column.DataType === "percent")
                {
                    if (!isNaN(+value))
                        args.rowData[column.Name] = +value / 100;
                }
                else if (column.DataType === "boolean")
                {
                    if (Utilities.IsNullOrUndefined(value))
                        args.rowData[column.Name] = "";
                    else
                        args.rowData[column.Name] = value.toString().toUpperCase();
                }

            });
        });

        const self = this;

        // igx sends the rows to the exporter in chunks of 100 through setImmediate, so the export
        // setup has to stay in place until the file itself is done
        exportService.exportEnded.pipe(take(1), takeUntil(destroyExportPipe$)).subscribe(() => endExport());
        this.ngZone.onError.pipe(filter(() => !!exportColumns), take(1), takeUntil(destroyExportPipe$)).subscribe(() => endExport());

        const reload = (this.model() && this.model().$applyProperty && this.model().MaxResultMessage) ?
            self.$commonDialogsService.showConfirmationMessage("The number of records in the grid has been limited. " +
                "Loading all available data may result in extended processing time. Would you like to load all available data?", "Alert", "Yes", "No")
                .then(() => true, () => false) : Promise.resolve(false);

        reload.then(x =>
        {
            if (x)
            {
                this.afterFetchCallback = () => doExport();

                this.model().OverrideResultLimit = true;
                this.model().$forExport = true;
                this.$metadataPropertyService.recalculateProperties(
                    this.model().$$TypeName,
                    this.model().$applyProperty,
                    this.model(),
                    this.model().$$parent,
                    this.model()[this.model().$applyProperty.Name],
                    undefined,
                    true,
                    true,
                    this.datacontroller()
                );
            }
            else
            {
                this.$gridLoadService.getData(this.grid, this.model(), this.viewMetadata(),
                    false, () => doExport(), null, null, null, null);
            }
        });


        function doExport(): void
        {
            let title: string;
            if (!Utilities.IsNullOrUndefinedOrEmpty(self.viewMetadata().Title))
                title = self.viewMetadata().Title;
            else if (!Utilities.IsNullOrUndefinedOrEmpty(self.viewMetadata()?.Title))
                title = self.viewMetadata().Title;
            else
                title = self.$omniBarService.title;
            title = Utilities.IsNullOrUndefinedOrEmpty(title) ? "ExportedData" : `${title} Export`;

            const isGrouped = !Utilities.IsNullOrUndefinedOrEmpty(self.grid.groupingExpressions);
            const args: IGridToolbarExportEventArgs = <IGridToolbarExportEventArgs>{};
            if (type === "excel")
                args.options = self.$gridExportService.createExcelExportOptions(title, isGrouped);
            else
            {
                args.options = new IgxCsvExporterOptions(title, CsvFileTypes.CSV);
                args.options.exportSummaries = false;
            }
            args.options.ignoreColumnsVisibility = true;

            const data = self.$providerStorageService.GridData[self.providerId];
            self.setColumnSummaries(self.$providerStorageService.ColumnSummaries[self.providerId], data);
            self.remoteData.next(data);
            self.cdr.detectChanges();

            // the columns are in their final shape only now that everything has been fetched
            exportColumns = self.$gridExportService.configureExportColumns(self.grid, selectedRowIds);
            cellFormatColumns = cellFormatColumns.filter(x => x.Name !== exportColumns.groupCaptionColumn?.field);

            try
            {
                exportService.export(self.grid, args.options);
            }
            catch (error)
            {
                endExport();
                throw error;
            }
        }

        function endExport(): void
        {
            exportColumns?.restoreFormatters();
            self.toolbar.showProgress = false;
            destroyExportPipe$.next();
            destroyExportPipe$.complete();
        }
    }

    get skipSort(): boolean
    {
        return (this.isAdding() && this.isGridCellEdit()) || this.cellEditing || this.activatingCellEdit;
    }

    cellEditAfterAdd(): void
    {
        // Navigation to the new row is async; without this the post-fetch scroll restore snaps the grid back before edit mode begins
        this.holdScrollPosition = true;
        this.cellAdding = false;
        this.$cellEditService.CellEditAfterAdd[this.providerId] = false;
        const rowIndex =
            this.viewMetadata().GridProperties.AddToTop || this.grid.data.length === 0
                ? 0
                : this.grid.data.length - 1;

        const position = this.$cellEditService.GetFirstEditableCell(this.grid, this.viewMetadata(), rowIndex, 0);
        if (!position)
        {
            this.isAdding.set(false);
            return;
        }
        this.setModelForCellEdit(this.$providerStorageService.GridData[this.providerId][rowIndex].Id, this.grid.columns[position.visibleColumnIndex].field);
        this.setMetadataForCellEdit(this.grid.columns[position.visibleColumnIndex].field);
        if (!this.viewMetadata().GridProperties.AddToTop && !this.editFirstCellAfterAdd)
        {
            if (this.grid.data.length < this.totalCount())
            {
                this.editFirstCellAfterAdd = true;
                this.fetch(false, false, false, false, true);
                return;
            }
        }
        this.editFirstCellAfterAdd = false;
        this.activatingCellEdit = true;
        //Clear a frame after activation so edit mode's own cellEditing flag has taken over the guard
        this.$cellEditService.ActivateEditableCell(this.grid, position, true, () => requestAnimationFrame(() => this.activatingCellEdit = false));
        this.isAdding.set(false);
        this.fetching.set(false);
    }

    cellEditKeyDown(event: KeyboardEvent): void
    {
        if (event.code !== "ArrowUp" && event.code !== "ArrowDown")
            return;

        this.disableGridScroll(false);

        const propertyMetadata = this.cellEditMetadata.Properties.find(p => p.Name === this.grid.columns[this.currentColumnIndex].field);
        const inputType = propertyMetadata?.InputType;

        if (!this.$cellEditService.VerticalEditNavigationAllowed(event.code, this.$providerStorageService.GridData[this.providerId], this.currentRowIndex, inputType))
            return;
        this.$cellEditService.EditNextVerticalCell(this.grid, this.viewMetadata(), this.currentRowIndex, this.currentColumnIndex, event.code === "ArrowUp");

        this.disableGridScroll(true);
    }

    currentColumnIndex: number;
    currentRowIndex: number;

    editCheckbox(cell: CellType): void
    {
        const self = this;
        const field = cell.column.field;
        const columnId = cell.id.columnID;
        this.currentColumnIndex = columnId;
        if (!this.isGridCellEdit() || !this.$cellEditService.AllowCellEdit(this.grid, this.viewMetadata(), this.currentColumnIndex, field, this.grid.getCellByColumn(cell.id.rowIndex, field)))
            return;
        requestAnimationFrame(() =>
        {
            if (!this.cellEditing)
            {
                self.setModelForCellEdit(cell.id.rowID, field);
                self.setMetadataForCellEdit(field);
                const row = self.$providerStorageService.GridData[self.providerId].find(x => x.Id === cell.id.rowID);
                self.$gridLayoutService.SetScrollPosition(self.providerId, row, self.grid);
                const newValue = !this.cellEditModel()[field];
                this.cellEditModel()[field] = newValue;
                row[field] = newValue;
                cell.editValue = newValue;
                self.$cellEditService.RecalculateAfterCellEdit(self.cellEditModel(), self.cellEditMetadata, field, self.currentEditCellOriginalModelValue, true, self.datacontroller());
                self.$gridEntityService.fireModelChangedListeners(self.providerId);
                self.setCellClasses();
                self.grid.cdr.detectChanges();
                self.exitCellEdit();
                self.holdScrollPosition = true;
            }
        });
    }

    parseBooleanValue(value: any): boolean
    {
        if (typeof value === "string")
            return value === "true" || value === "True" || value === "1";
        return value;
    }

    handleGridKeydown(evt: IGridKeydownEventArgs): void
    {
        const key = evt.event["key"].toLowerCase();
        const shift = evt.event["shiftKey"];

        if (key !== "tab" || !evt.target.editMode)
            return;

        evt.cancel = true;
        this.flushCellEditTransitions();

        const propertyMetadata = this.cellEditMetadata.Properties.find(p => p.Name === this.grid.columns[this.currentColumnIndex].field);
        if (propertyMetadata?.InputType === "Check")
            this.$cellEditService.ViewOnly = {};

        const activeCell = {
            row: evt.target.row.index as number,
            column: evt.target.visibleColumnIndex as number
        };

        const rowIndex = activeCell.row;
        const isAtFirstColumn = activeCell.column === 0;
        const isFirstRow = activeCell.row === 0;

        let rowIndexForPermission = rowIndex;
        let followingPosition = null;

        if (shift)
        {
            if (isAtFirstColumn && !isFirstRow)
                rowIndexForPermission = rowIndex - 1;
            followingPosition = this.$cellEditService.GetPreviousEditableCell(this.grid, this.viewMetadata(), this.currentColumnIndex, rowIndex, activeCell.column, rowIndexForPermission);
        }
        else
            followingPosition = this.$cellEditService.GetNextEditableCellSafe(this.grid, this.viewMetadata(), this.currentColumnIndex, activeCell.row, activeCell.column);
        if (followingPosition === null)
        {
            const event = <IGridEditEventArgs>{
                event: evt.event,
                column: { field: this.grid.columns[this.currentColumnIndex].field },
                cellID: { rowIndex: rowIndex },
                rowData: this.grid.data[rowIndex]
            };
            this.leaveCellEdit(false, event);
        }
        else
            this.$cellEditService.ActivateEditableCell(this.grid, followingPosition);
    }

    onCellEditEnter(event: IGridEditEventArgs): void
    {
        if (!this.$metadataViewService.IsCellEditGrid(this.viewMetadata()))
        {
            event.cancel = true;
            return;
        }

        this.currentColumnIndex = event.cellID.columnID;
        if (!this.$cellEditService.AllowCellEdit(this.grid, this.viewMetadata(), this.currentColumnIndex, event.column.field, this.grid.getCellByColumn(event.cellID.rowIndex, event.column.field), false))
        {
            event.cancel = true;
            const position = this.$cellEditService.GetNextEditableCellSafe(this.grid, this.viewMetadata(), this.currentColumnIndex, event.cellID.rowIndex, this.grid.columns[this.currentColumnIndex].visibleIndex);
            if (position)
                requestAnimationFrame(() => this.$cellEditService.ActivateEditableCell(this.grid, position, true));
        }
        else
            this.beginCellEdit(event);
    }

    onScroll(event: IGridScrollEventArgs): void
    {
        if (this.cellEditing)
        {
            (event.event.target as HTMLElement).scrollTop = this._gridScrollTop;
        }
    }

    private _gridScrollTop = 0;

    //Exits from select/date/time cells and the start of the next cell's edit wait a few ms, but a busy main thread can run those
    //timers hundreds of ms late. By then igx has moved on to later cells, and a late start would put the earlier cell back as the
    //current edit. Running any still pending before the next cell move keeps our edit state in step with igx.
    private readonly pendingCellEditTransitions: { timer: ReturnType<typeof setTimeout>; run(): void }[] = [];

    private deferCellEditTransition(run: () => void, delayMs: number): void
    {
        const pending = { timer: undefined as ReturnType<typeof setTimeout>, run };
        pending.timer = setTimeout(() =>
        {
            this.pendingCellEditTransitions.splice(this.pendingCellEditTransitions.indexOf(pending), 1);
            run();
        }, delayMs);
        this.pendingCellEditTransitions.push(pending);
    }

    private flushCellEditTransitions(): void
    {
        while (this.pendingCellEditTransitions.length)
        {
            const pending = this.pendingCellEditTransitions.shift();
            clearTimeout(pending.timer);
            pending.run();
        }
    }

    disableGridScroll(disable: boolean): void
    {
        const scrollBar = this.grid.nativeElement.querySelector(".igx-vhelper--vertical") as HTMLElement;
        this._gridScrollTop = scrollBar.scrollTop;
        scrollBar.style.pointerEvents = disable ? "none" : "";
    }

    beginCellEdit(event: IGridEditEventArgs): void
    {
        const self = this;
        this.grid.sortingExpressions = [];
        this.$gridSortService.SortingExpressions[this.providerId] = [];

        this.disableGridScroll(true);

        if (this.delayUpdate)
        {
            this.cellEditTransition.set(true);
            this.deferCellEditTransition(() => self.beginCellEditImpl(event), 15);
        }
        else
            self.beginCellEditImpl(event);
        this.delayUpdate = false;
    }

    beginCellEditImpl(event: IGridEditEventArgs): void
    {
        const self = this;
        const rowId = event.rowID;
        const rowIndex = event.cellID.rowIndex;
        const columnField = event.column.field;
        const columnId = event.cellID.columnID;
        const cell = this.grid.getCellByColumn(rowIndex, columnField);
        this.$cellEditService.CellEditCell[this.providerId] = cell;
        this.setModelForCellEdit(rowId, columnField);
        this.setMetadataForCellEdit(columnField);
        this.currentColumnIndex = columnId;
        if (this.currentRowIndex !== rowIndex)
            this.$cellEditService.ClearCellEditErrors(this.cellEditModel(), this.viewMetadata().GridProperties.ClassMetadata.Properties);
        this.currentRowIndex = rowIndex;
        this.cellEditTransition.set(false);
        this.cellEditing = true;
        this.$cellEditService.ViewOnly = {};
        const propertyMetadata = this.cellEditMetadata.Properties.find(p => p.Name === columnField);
        this.cellEditInPopup.set(this.$metadataInputTypeHelperService.isTextArea(propertyMetadata.InputType) || propertyMetadata.EditInPopup);
        const inputType = propertyMetadata?.InputType;
        if (self.$metadataInputTypeHelperService.isCheck(inputType) && event.event instanceof PointerEvent)
        {
            if (this.recalculating())
                this.runCheckboxEdit.set(() => this.checkboxEdit(cell, columnField));
            else
                this.checkboxEdit(cell, columnField);
            return;
        }
        this.handleEditPopover();
    }

    checkboxEdit(cell: CellType, columnField: string): void
    {
        const row = this.$providerStorageService.GridData[this.providerId].find(x => x.Id === cell.id.rowID);
        const newValue = !this.cellEditModel()[columnField];
        this.cellEditModel()[columnField] = newValue;
        row[columnField] = newValue;
        this.$cellEditService.RecalculateAfterCellEdit(this.cellEditModel(), this.cellEditMetadata, columnField, this.currentEditCellOriginalModelValue, true, this.datacontroller());
        this.handleEditPopover();
    }

    onPopoverBeforeToggle(event: CustomEvent): void
    {
        const inlineOpen = this.cellEditInlinePopover?.open;
        const popupOpen = this.cellEditPopupPopover?.open;
        if (inlineOpen || (this.cellEditInPopup() && popupOpen))
            event.preventDefault();
    }

    handleEditPopover(): void
    {
        setTimeout(() =>
        {
            if (this.cellEditInput)
            {
                const popover = this.cellEditInPopup()
                    ? this.cellEditPopupPopover
                    : this.cellEditInlinePopover;
                if (!popover)
                    return;
                if (popover.open)
                    popover.open = false;

                popover.anchorElement = this.cellEditInput.nativeElement as HTMLElement;
                popover.open = true;

                if (this.cellEditInPopup())
                    requestAnimationFrame(() => this.cellEditPopupInput.nativeElement.querySelector('input,textarea')?.focus());
            }
        }, 50);
    }

    setModelForCellEdit(id: string, property: string): void
    {
        const row = this.$providerStorageService.GridData[this.providerId].find(x => x.Id === id);
        const model = row["$$model"];
        this.cellEditModel.set(this.$gridMetadataService.getModelForProperty(this.viewMetadata().GridProperties.ClassMetadata, property, model));
        if (Utilities.IsNullOrUndefined(this.cellEditModel()))
            this.cellEditModel.set(<AugmentedModelObject>{});
        this.currentEditCellOriginalModelValue = model[property];
        this.currentEditCellOriginalDisplayValue = row[property];
        this.currentEditCellPropertyName = property;
        this.datacontroller().SetInitialModCountEntity(this.cellEditModel(), false);
    }

    setMetadataForCellEdit(property: string): void
    {
        this.cellEditMetadata = this.$gridMetadataService.getClassMetadataForProperty(
            this.viewMetadata().GridProperties.ClassMetadata,
            property
        );
    }

    leaveCellEdit(cancel: boolean, event: IGridEditEventArgs = null): void
    {
        const self = this;
        this.flushCellEditTransitions();
        if (cancel) this.$cellEditService.CellEditCancel[this.providerId] = true;
        self.endCellEditImpl(self.$cellEditService.CellEditCell[self.providerId].column.field, self.$cellEditService.CellEditCell[self.providerId].row.key, event);
        self.grid.crudService.endEditMode();
        self.grid.notifyChanges();
    }

    endCellEdit(event: IGridEditDoneEventArgs): void
    {
        const self = this;
        this.flushCellEditTransitions();
        const inputType = this.cellEditMetadata.Properties.find(p => p.Name === event.column.field)?.InputType;
        if (inputType === "Button" && event)
        {
            this.handleLastCellEdit(event);
            return;
        }

        let rowId = event.rowKey;
        if (Utilities.IsNullOrUndefined(rowId))
        {
            rowId = this.grid.getRowByIndex(event.cellID.rowIndex).key;
        }

        const selectType = this.$metadataInputTypeHelperService.isSelect(inputType) || this.$metadataInputTypeHelperService.isTypeAhead(inputType);

        if (
            (selectType ||
                this.$metadataInputTypeHelperService.isDate(inputType) ||
                this.$metadataInputTypeHelperService.isTime(inputType) ||
                this.$metadataInputTypeHelperService.isTime24(inputType))
        )
        {
            this.delayUpdate = !this.$cellEditService.CellEditCancel[this.providerId];
            //TODO: if this is higher it "works" but screws the timing of currentEditCellOriginalModelValue setting for recalc, maybe fix as separate issue since it is already happening
            this.deferCellEditTransition(() => self.endCellEditImpl(event.column.field, rowId, event), 10);
        }
        else self.endCellEditImpl(event.column.field, rowId, event);
    }

    endCellEditImpl(field: string, rowId: string, event: IGridEditDoneEventArgs = null): void
    {
        try
        {
            this.disableGridScroll(false);

            const row = this.$providerStorageService.GridData[this.providerId].find(x => x.Id === rowId);
            if (this.$cellEditService.CellEditCancel[this.providerId])
            {
                if (row["$$model"]["$pristine"])
                {
                    this.datacontroller().Remove(
                        this.viewMetadata().GridProperties.ClassMetadata.TypeName,
                        row["$$model"],
                        this.$metadataViewService.createFilterString(row["$$model"], this.viewMetadata())
                    );
                    this.$gridEventService.fireTableEntitiesChangedListeners(this.providerId);
                }
                else if (!this.recalculatedDuringEdit)
                {
                    row["$$model"][field] = this.currentEditCellOriginalModelValue;
                    row[field] = this.currentEditCellOriginalDisplayValue;
                }
                return;
            }
            row["$$model"]["$pristine"] = false;
            this.$cellEditService.SetCellEditErrors(row["$$model"], this.viewMetadata().GridProperties.ClassMetadata.Properties);
            if (this.cellEditModel()[field] !== this.currentEditCellOriginalModelValue)
                this.$cellEditService.RecalculateAfterCellEdit(this.cellEditModel(), this.cellEditMetadata, field, this.currentEditCellOriginalModelValue, false, this.datacontroller());

            const newValue = this.cellEditModel()[field];
            const propertyMetadata = this.cellEditMetadata.Properties.find(p => p.Name === field);

            this.$cellEditService.AfterCellEdit(
                this.$cellEditService.CellEditCell[this.providerId],
                row,
                propertyMetadata,
                this.viewMetadata(),
                this.grid,
                this.providerId,
                newValue
            );
            this.updateSummaries(field, this.currentEditCellOriginalModelValue, newValue);
            if (event) this.handleLastCellEdit(event);
        }
        finally
        {
            this.endCellEditFinal();
        }
    }

    endCellEditFinal(): void
    {
        this.setCellClasses();
        this.exitCellEdit();
        if (this.cellEditInlinePopover)
            this.cellEditInlinePopover.open = false;
        if (this.cellEditPopupPopover)
            this.cellEditPopupPopover.open = false;
        this.cellEditInPopup.set(false);
    }

    exitCellEdit(): void
    {
        this.$cellEditService.ViewOnly = {};
        if (!this.cellEditAddWaitForRecalculate)
            this.$cellEditService.CellEditCell[this.providerId] = undefined;
        this.$cellEditService.CellEditCancel[this.providerId] = false;
        this.currentEditCellOriginalModelValue = undefined;
        this.currentEditCellOriginalDisplayValue = undefined;
        this.currentEditCellPropertyName = undefined;
        this.cellEditing = false;
        this.recalculatedDuringEdit = false;
    }

    handleLastCellEdit(event: IGridEditDoneEventArgs): void
    {
        if (Utilities.IsNullOrUndefined(event.event) || event.event.type !== "keydown") return;
        const key = event.event as KeyboardEvent;
        if (key.key === "Tab" && !key.shiftKey && this.$cellEditService.IsLastEditableColumn(this.grid, this.viewMetadata(), event.cellID.rowIndex, this.grid.columns[this.currentColumnIndex].visibleIndex) && event.cellID.rowIndex === this.grid.totalRowsCountAfterFilter - 1)
        {
            if (this.AutoAddNewRow && this.canAdd())
            {
                if (this.cellEditMetadata.Properties.find(p => p.Name === event.column.field).RecalculateOnChange && this.currentEditCellOriginalModelValue !== event.rowData[event.column.field])
                    this.cellEditAddWaitForRecalculate = true;
                else
                    this.addItem(false);
            }
        }
    }

    updateSummaries(field: string, originalValue: number, newValue: number): void
    {
        const self = this;
        if (this.$providerStorageService.GridData[this.providerId]?.length > 0)
        {
            const summaryColumns = this.$gridMetadataService.getSummaryColumns(
                this.viewMetadata().GridProperties.ClassMetadata
            );
            if (summaryColumns?.length !== 0)
            {
                const col = summaryColumns.find(c => c === field);
                if (col)
                {
                    self.$providerStorageService.ColumnSummaries[self.providerId][field] -= originalValue;
                    self.$providerStorageService.ColumnSummaries[self.providerId][field] += newValue;
                }

                this.$gridSummaryService
                    .getColumnSummaries(this.providerId, this.grid, this.viewMetadata(), this.metadataServerPaging(), summaryColumns, false, this.model())
                    .then(result =>
                    {
                        self.setColumnSummaries(result, self.grid.data);
                        self.grid.summaryService.clearSummaryCache();
                        self.grid.summaryService.calculateSummaries(
                            self.grid.summaryService.rootSummaryID,
                            self.grid.data,
                            undefined
                        );
                    });
            }
        }
    }

    formatAction(column: any): boolean
    {
        return (
            column.IsCurrency ||
            column.DataType === "number" ||
            column.DataType === "nonDelineatedNumber" ||
            column.DataType === "percent" ||
            column.DataType === "date" ||
            column.DataType === "dateTime" ||
            column.DataType === "time" ||
            column.DataType === "time24"
        );
    }

    applyColumnFormatter(c: any, column: IgxColumnComponent): void
    {
        if (c.DataType === "date")
            column.formatter = (value: any) => value ? safeFormat(value, v => formatDate(v, "MM/dd/yyyy", this.locale)) : "";
        else if (c.DataType === "dateTime")
        {
            const format = this.timeFormat(c.Name, true);
            column.defaultDateTimeFormat = format;
            column.formatter = (value: any) => value ? safeFormat(value, v => formatDate(v, format, this.locale)) : "";
        }
        else if (c.DataType === "percent")
            column.formatter = (value: any) => safeFormat(value, v => this.$gridFormatService.numberFormatter(v, c, true));
        else if (c.DataType === "number")
            column.formatter = c.IsCurrency
                ? (value: any) => safeFormat(value, v => this.$gridFormatService.currencyFormatter(v, c))
                : (value: any) => safeFormat(value, v => this.$gridFormatService.numberFormatter(v, c, false));
        else if (c.DataType === "nonDelineatedNumber")
            column.formatter = (value: any) => safeFormat(value, v => this.$gridFormatService.nonDelineatedNumberFormatter(v, c));

        function safeFormat(value: any, format: (v: any) => string): string
        {
            try
            {
                return format(value);
            }
            catch
            {
                // unformattable value (bad date, malformed DigitsInfo); just show raw value
                return Utilities.IsNullOrUndefined(value) ? "" : value.toString();
            }
        }
    }

    MaybeRunRecalculate(): void
    {
        if (!this.runRecalculate)
            return;
        this.runRecalculate = false;
        if (!this.viewMetadata().SubPropertyRecalculateOnFilter)
            return;

        let type = this.model().$$TypeName;
        if (!type && this.datacontroller())
            type = this.datacontroller().Type;
        const propertyMetadata = <PropertyMetadata>{
            Name: this.viewMetadata().SubPropertyName,
            RecalculateOnChange: this.$metadataPropertyService.getPropertiesToRecalculate(this.viewMetadata().SubPropertyRecalculateOnFilter),
            RecalculateToIgnore: this.viewMetadata().SubPropertyRecalculateOnFilter.PropertiesToIgnore
        };

        //dont love this, but this may never be used anywhere but one place, so dont want to rewrite a whole new pattern yet
        this.model()[this.viewMetadata().SubPropertyName].map(x => x["inFilter"] = x.$inFilter ? true : false);

        this.$metadataPropertyService.recalculateProperties(
            type,
            propertyMetadata,
            this.model(),
            this.model().$$parent,
            this.model()[this.viewMetadata().SubPropertyName].filter(x => x.$inFilter),
            undefined,
            true,
            true,
            this.datacontroller());
    }

    get showExcelStyleSearch(): boolean
    {
        return !this.metadataServerPaging();
    }

    get showCustomFilter(): boolean
    {
        return this.metadataServerPaging();
    }

    get showGridSearch(): boolean
    {
        return !this.metadataServerPaging();
    }

    toggleTableOptionsOpen(): void
    {
        this.tableOptionsOpen.update(x => !x);
    }

    clearCustomFilter(): void
    {
        if (this.showCustomFilter)
            this.$gridFilterService.clearCustomFilter();
    }

    private readonly _cellEditing = signal(false);
    private readonly _cellAdding = signal(false);

    get cellEditing(): boolean
    {
        return this._cellEditing();
    }

    set cellEditing(value: boolean)
    {
        const viewMetadata = this.viewMetadata();
        if (viewMetadata)
            viewMetadata["$$cellEditing"] = value;
        this._cellEditing.set(value);
    }

    get cellAdding(): boolean
    {
        return this._cellAdding();
    }

    set cellAdding(value: boolean)
    {
        const viewMetadata = this.viewMetadata();
        if (viewMetadata)
            viewMetadata["$$cellAdding"] = value;
        this._cellAdding.set(value);
    }

    get layoutOptions(): IMenuOption[]
    {
        return [{
            value: "load",
            label: "Load",
        },
        {
            value: "save",
            label: "Save",
        },
        {
            value: "delete",
            label: "Delete",
        },
        {
            value: "restore",
            label: "Restore",
        }];
    }

    get showTitle(): boolean
    {
        return (
            this.initialized() &&
            !Utilities.IsNullOrUndefinedOrEmpty(this.viewMetadata().Title) &&
            this.displayHeader()
        );
    }

    get showH1Title(): boolean
    {
        if (this.combinedViewTable)
        {
            const showForCombined = this.viewMetadata()["$$ParentView"].Views.length === 1;
            return !this.showTitle && showForCombined && this.displayHeader();
        }

        return !this.showTitle && this.displayHeader();
    }

    get h1Title(): string
    {
        return this.$omniBarService.title;
    }

    readonly isViewOnly: Signal<boolean> = computed(() =>
    {
        return this.viewOnly() || this.recalculating();
    });

    layoutAction(_e: unknown): void
    {
        const action = this.gridLayoutDropdown.selectedItem ? this.gridLayoutDropdown.selectedItem.value : "";
        switch (action)
        {
            case "load":
                this.loadGridState(false);
                break;
            case "save":
                this.saveGridState(false);
                break;
            case "delete":
                this.deleteGridState();
                break;
            case "restore":
                this.restoreGridState();
                break;
        }
        this.gridLayoutDropdown.clearSelection();
    }

    saveGridState(local: boolean): void
    {
        this.$gridLayoutService.SaveLayout(this.providerId, this.state, local);
    }

    loadGridState(local: boolean): void
    {
        const self = this;
        const providerId = this.providerId ? this.providerId : self.viewMetadata()?.GridProperties["$providerId"];
        if (providerId)
            this.$gridLayoutService.LoadLayout(providerId, local)
                .then(layout =>
                {
                    if (layout)
                    {
                        const parsed = layout.split("|");
                        layout = parsed[0];
                        if (local && !self.firstLayoutLoadFromCache)
                            self.updateLayout(layout);
                        else
                            self.$gridLayoutService.SetState(self.grid, self.state, layout, self.columns());
                        self.setFilterDisplay();
                        self.firstLayoutLoadFromCache = false;
                    }
                });
    }

    firstLayoutLoadFromCache: boolean = true;

    updateLayout(layout: string): void
    {
        //if column visibility changed, update layout
        const cachedState = Utilities.parseJson(layout);
        let hasChanged = false;
        if (!cachedState || !cachedState["columns"])
            return;
        cachedState.columns.forEach(column =>
        {
            const initialHidden = column.hidden;
            const match = this.columns().find(x => x.Name === column.field);
            if (match && !match.AlwaysHidden && (!match.Visible || column.hidden))
                column.hidden = false;
            hasChanged = hasChanged || initialHidden !== column.hidden;

        });
        if (hasChanged)
        {
            const newLayout = Utilities.stringify(cachedState);
            this.$gridLayoutService.SetState(this.grid, this.state, newLayout, this.columns());
            this.$gridLayoutService.LocalCachedLayout[this.providerId] = newLayout;
            const self = this;
            self.grid.markForCheck();
        }
    }

    deleteGridState(): void
    {
        this.$gridLayoutService.DeleteLayout(this.providerId);
    }

    restoreGridState(): void
    {
        this.restoringLayout.set(true);
        this.grid.sortingExpressions = [];
        this.$gridSortService.SortingExpressions[this.providerId] = [];
        this.grid.groupingExpressions = [];
        for (let i = this.grid.pinnedColumnsCount - 1, l = 0; i >= l; i--)
        {
            this.grid.pinnedColumns[i].unpin();
        }
        this.grid.columns.forEach(x => x.hidden = false);
        requestAnimationFrame(() => this.grid.columns.forEach(x => this.setColumnWidth(x.field)));

        let prevColumn: IgxColumnComponent;
        this.columns().forEach(x =>
        {
            const gridColumn = this.grid.columns.find(c => c.field === x.Name);
            if (prevColumn)
                this.grid.moveColumn(gridColumn, prevColumn, 1);
            else
                gridColumn.move(0);
            prevColumn = gridColumn;
        });

        if (this.showIdTemplate())
        {
            const idColumn = this.grid.columns.find(c => c.field === "Id");
            if (idColumn)
            {
                idColumn.hidden = true;
                idColumn.move(0);
            }
        }

        this.grid.deselectAllColumns();
        this.grid.deselectAllRows();
        this.grid.clearCellSelection();
        this.grid.moving = true;
        this.restoringLayout.set(false);
    }

    onColumnInit(columnComponent: IgxColumnComponent): void
    {
        if (!this.columns())
            return;
        const column = this.columns().find(x => x.Name === columnComponent.field);
        if (!column)
            return;
        this.setTemplates(columnComponent);

        this.setupSummaryColumn(column.Name);
        const hasSummary = this.hasSummary(column.Name);
        columnComponent.hasSummary = hasSummary;
        columnComponent.summaries = hasSummary ? this.useSummary(column.IsCurrency, column.Name) : undefined;

        this.applyColumnFormatter(column, columnComponent);

        if (!columnComponent.cellClasses)
            columnComponent.cellClasses = this.cellClasses;
    }

    getColumnDataType(column): string
    {
        return column.DataType === "nonDelineatedNumber" ? "number" : column.DataType;
    }

    setTemplates(columnComponent: IgxColumnComponent): void
    {
        if (!columnComponent.inlineEditorTemplate)
            columnComponent.inlineEditorTemplate = this.cellEditTemplate;
    }

    private columnValuesStrategyImpl(
        column: IgxColumnComponent,
        columnExprTree: IFilteringExpressionsTree,
        done: (uniqueValues: any[]) => void
    ): void
    {
        if (this.showExcelStyleSearch)
            this.getColumnData(column, columnExprTree, this.filterStrategy, uniqueValues => done(uniqueValues));
    }

    public getColumnData(
        column: IgxColumnComponent,
        columnExprTree: IFilteringExpressionsTree,
        filteringStrategy: IFilteringStrategy,
        done: (colVals: any[]) => void
    ): void
    {
        requestAnimationFrame(() =>
        {
            if (this.fetching() || this.lookupLoading(this.columns().filter(col => col.Name === column.field)[0]))
                return;
            this.getDataForFilter(column.field, columnExprTree)
                .then(data =>
                {
                    const filteredData = filteringStrategy.filter(data, columnExprTree, null, null);
                    let columnValues = filteredData.map(record => record[column.field]);
                    if (column.dataType === "date")
                        columnValues = columnValues.filter((date, i, self) => self.findIndex(d => d?.getTime() === date?.getTime()) === i);
                    done([...new Set(columnValues)]);
                });
        });
    }

    getDataForFilter(columnName: string, columnExprTree: IFilteringExpressionsTree): Promise<any[]>
    {
        const column = this.columns().filter(col => col.Name === columnName)[0];
        if (!column || this.metadataServerPaging() || this.lookupLoading(this.columns().filter(col => col.Name === columnName)[0]))
            return null;
        const filteredColumns: string[] = columnExprTree.filteringOperands.map(f =>
        {
            const c = f.fieldName;
            const filteredColumn = this.columns().find(col => col.Name === c);
            if (filteredColumn?.["ParentPropertyName"])
                return `${filteredColumn["ParentPropertyName"]}.${c}`;
            return c;
        });
        const colName = column["ParentPropertyName"] ? `${column["ParentPropertyName"]}.${columnName}` : columnName;
        filteredColumns.push(colName);
        return this.$gridProcessingService
            .buildGridRows(
                this.$providerStorageService.LocalEntitiesStore[this.providerId],
                this.viewMetadata(),
                null,
                filteredColumns
            )
            .then(rows =>
            {
                return rows;
            });
    }

    editFromContext(row: any): void
    {
        const cell = this.grid.getCellByKey(row.Id, "Id");
        if (cell)
            this.editRow({
                cell: cell,
                event: null
            });
        else
            this.editSingle(row, row.Id);
    }

    editRow(event: IGridCellEventArgs): void
    {
        if (!this.viewMetadata().GridProperties.CanEdit)
        {
            return cancelEdit();
        }

        const id = event.cell?.id?.rowID;
        if (!id)
            return cancelEdit();

        const row = this.grid.data?.find(x => x.Id === id);
        if (!row)
            return cancelEdit();

        this.$gridLayoutService.SetScrollPosition(this.providerId, row, this.grid);
        this.$providerStorageService.LastEditedRecordIndex[this.providerId] = this.grid.data?.findIndex(x => x.Id === id) ?? -1;

        if (row.FeatureAction)
        {
            this.executeFeatureAction(row);
            return cancelEdit();
        }

        if (row.Disabled || (!this.$gridMetadataService.canDo("Edit", row["$$model"], this.isViewOnly(), this.viewMetadata()) && !this.isViewOnly()))
        {
            if (!this.doubleClickView)
                return cancelEdit();
        }

        if (this.isGridCellEdit())
        {
            return;
        }

        if (!this.viewMetadata().EditAction)
            this.$multipleEntityEditService.ProviderId = this.providerId;
        if (this.$gridFilterService.allowShowRecent(this.viewMetadata()))
            this.$gridFilterService.addToRecentIds(this.viewMetadata().GridId, row.Id);
        if (this.useRepository())
        {
            if (this.multipleEdit() && this.$providerStorageService.LocalEntitiesStore[this.providerId]?.length > 0)
                this.editMultiple(row, this.doubleClickView);
            else
                this.editSingle(row, id);
        }

        function cancelEdit(): void
        {
            if (event.event?.preventDefault)
                event.event.preventDefault();
        }
    }

    get doubleClickView(): boolean
    {
        return (
            this.viewMetadata().ViewOnly ||
            this.viewMetadata().EditForm?.ViewOnly ||
            this.viewMetadata().EditFormViewOnly
        ) && !this.disableViewAction() && !this.$metadataViewService.IsCellEditGrid(this.viewMetadata());
    }

    executeFeatureAction(row: any): void
    {
        const self = this;
        if (self.$metadataActionService.OpenNewTabIfNeeded(row["$$model"], row.FeatureAction) === true)
            return;
        this.$gridEntityService.fetchRowModel(row, this.datacontroller(), this.viewMetadata())
            .then(x => executeAction(x));
        function executeAction(model: any): void
        {
            self.$metadataActionService.ExecuteAction(model, row.FeatureAction, self.datacontroller());
        }
    }

    executeAction(row: any, action: MetadataAction): void
    {
        const self = this;
        if (self.$metadataActionService.OpenNewTabIfNeeded(row["$$model"], action) === true)
            return;
        this.$gridEntityService.fetchRowModel(row, this.datacontroller(), this.viewMetadata())
            .then(x => executeAction(x));
        function executeAction(model: any): void
        {
            self.$metadataActionService.ExecuteAction(model, action, self.datacontroller());
        }
    }

    viewSingle(row: any, id: any): void
    {
        this.doRepositoryFunction("View", <RowCommandRow>{
            id: id,
            model: row["$$model"],
        });
    }

    editSingle(row: any, id: any): void
    {
        this.doRepositoryFunction("Edit", <RowCommandRow>{
            id: id,
            model: row["$$model"],
        });
    }

    editMultiple(row: any, viewOnly: boolean): void
    {
        const entities = this.$providerStorageService.LocalEntitiesStore[this.providerId].filter(e =>
        {
            const isInFilter = e["$inFilter"] && e.SerializedRowState !== "Deleted";
            const canOpen = viewOnly || this.$gridMetadataService.canDo("Edit", e, this.isViewOnly(), this.viewMetadata());
            return canOpen && isInFilter;
        });

        if (!this.viewMetadata().EditAction)
        {
            this.$multipleEntityEditService.EditableEntities = entities;
            this.$multipleEntityEditService.CurrentEditIndex = this.$multipleEntityEditService.EditableEntities.indexOf(row["$$model"]);
        }
        else
            this.$multipleEntityEditService.editActionEntity = row["$$model"];

        this.doRepositoryFunction("EditMultiple", null);
    }

    timeFormat(field: string, isDateTime: boolean = false): string
    {
        const dateFormat = isDateTime ? "MM/dd/yyyy " : "";
        return this.allowSeconds(this.getRegEx(field)) ? `${dateFormat}hh:mm:ss a` : `${dateFormat}hh:mm a`;
    }

    timeFormat24(field: string): string
    {
        return this.allowSeconds(this.getRegEx(field)) ? "HH:mm:ss" : "HH:mm";
    }

    lookupLoading(column: any): boolean
    {
        return column.LookupType && this.$gridLookupService.lookupsLoaded[column.LookupType] === false;
    }

    private getRegEx(field: string): string
    {
        return this.$gridMetadataService.getProperty(
            this.viewMetadata()?.GridProperties?.ClassMetadata,
            field
        )?.Regex;
    }

    private allowSeconds(regex): boolean
    {
        if (Utilities.IsNullOrUndefined(regex))
        {
            return false;
        }
        const pattern = new RegExp(regex);
        return pattern?.test("12:00:00 AM");
    }

    addBooleanFilter(field: string, value: any): void
    {
        this.grid.filteringLogic = FilteringLogic.Or;
        this.grid.filter(field, value, IgxBooleanFilteringOperand.instance().operations[1]);
    }

    clearFilter(): void
    {
        this.$gridFilterService.searchText = "";
        this.$gridFilterService.clearFilter(this.grid);
    }

    trackByFn(_index: number, item: any): string
    {
        return item.Name;
    }

    toggleHistory(showHistory: boolean): void
    {
        this.fetching.set(true);
        this.recentLoaded.set(showHistory);
        this.$providerStorageService.Refetch[this.providerId] = true;
        this.fetch(true, showHistory, true);
    }

    pinnable(column: any): boolean
    {
        return !column.AlwaysHidden && !this.grid.columns.find(x => x.field === column.Name)?.hidden;
    }

    openColumnPinning(): void
    {
        const buttons = document.getElementsByName("btnColumnPinning");
        if (!Utilities.IsNullOrUndefined(buttons))
        {
            buttons.forEach(b =>
            {
                if ((b.parentNode as HTMLElement).closest("igx-grid").id === this.providerId)
                    b.click();
            });
        }
    }

    openColumnVisibility(): void
    {
        const buttons = document.getElementsByName("btnColumnHiding");
        if (!Utilities.IsNullOrUndefined(buttons))
        {
            buttons.forEach(b =>
            {
                if ((b.parentNode as HTMLElement).closest("igx-grid").id === this.providerId)
                    b.click();
            });
        }
    }

    toolbarButtonVariant(type: string): IconButtonVariant
    {
        switch (type)
        {
            case "visibility": {
                //TODO Find a better way to determine if id is auto-hidden
                const autoHiddenColumnCount = this.showIdTemplate() ? 2 : 1; //Selected and Id if it's available
                return this.grid.hiddenColumnsCount - autoHiddenColumnCount > 0 ? "tonal" : undefined;
            }
            case "pinning":
                return this.grid.pinnedColumnsCount > 0 ? "tonal" : undefined;

            case "history":
                return this.recentLoaded() ? "tonal" : undefined;

            default:
                return undefined;
        }
    }

    onKeyDown(eventArgs: KeyboardEvent): void
    {
        if ((eventArgs.key === "F10" && eventArgs.shiftKey === true))
        {
            this.onKeyUp(eventArgs);
        }

        if (this.isGroupedGrid && this.activeHeaderField && eventArgs.key === "Enter" && (eventArgs.target as HTMLElement).role !== "button")
        {
            this.toggleColumnGrouping(this.activeHeaderField, eventArgs);
            return;
        }

        if (!this.viewMetadata().GridProperties.CanEdit && (eventArgs.key === "Enter" || eventArgs.key === " "))
        {
            eventArgs.preventDefault();
            const cell = this.grid.selectedCells[0] as IgxGridCell;
            if (cell)
                this.doAction(cell);
        }
    }

    onKeyUp(eventArgs: KeyboardEvent): void
    {
        if (this.cellEditing)
        {
            if (eventArgs.code === "Escape")
            {
                this.leaveCellEdit(true);
            }
            return;
        }
        const key = eventArgs["key"].toLowerCase();
        const ctrl = eventArgs["ctrlKey"];

        if (ctrl && key === "home")
        {
            this.$metadataViewService.focusFirstMetadataInput(this.viewMetadata()["$$ParentView"] ? this.viewMetadata()["$$ParentView"] : this.viewMetadata(), this.model(), this.datacontroller());
            return;
        }

        if (eventArgs.code === "ContextMenu" || (eventArgs.key === "F10" && eventArgs.shiftKey === true))
        {
            const cell = document.querySelector("igx-grid-cell.igx-grid__td--selected");

            if (cell)
            {
                eventArgs.preventDefault();
                const cellRect = cell.getBoundingClientRect();

                const event = {
                    cell: this.grid.selectedCells[0],
                    event: {
                        clientX: cellRect.x + cellRect.width / 2,
                        clientY: cellRect.y + cellRect.height / 2
                    }
                };

                this.onContextMenu(event);
            }
        }

        if (this.viewMetadata().GridProperties.CanEdit && eventArgs.code === "Enter")
        {
            const event = {
                cell: this.grid.selectedCells[0],
                event: null
            };

            this.editRow(event);
        }
    }

    @ViewChild("contextMenuContainer", { read: ViewContainerRef }) contextMenuContainer: ViewContainerRef;
    private contextMenuComponentRef: ComponentRef<GridContextComponent>;

    onContextMenu(eventArgs: any): void
    {
        if (this.cellEditing || eventArgs.event.target?.nodeName.toLowerCase() === "forge-icon")
            return;

        this.grid.deselectAllRows();

        if (eventArgs.event.preventDefault)
            eventArgs.event.preventDefault();

        this.closeContextMenu();

        const row = eventArgs.cell.row.data;
        const position = {
            x: eventArgs.event.clientX,
            y: eventArgs.event.clientY
        };

        this.contextMenuComponentRef = this.contextMenuContainer.createComponent(GridContextComponent);
        this.contextMenuComponentRef.instance.row = row;
        this.contextMenuComponentRef.instance.viewMetadata = this.viewMetadata();
        this.contextMenuComponentRef.instance.position = position;
        this.contextMenuComponentRef.instance.isViewOnly = this.isViewOnly() || this.viewMetadata().EditForm?.ViewOnly || this.viewMetadata().EditFormViewOnly;
        this.contextMenuComponentRef.instance.disableViewAction = this.disableViewAction();
        this.contextMenuComponentRef.instance.useRepository = this.useRepository();

        this.contextMenuComponentRef.instance.actionSelected.pipe(take(1))
            .subscribe(action =>
            {
                this.handleContextMenuAction(action);
            });

        this.contextMenuComponentRef.instance.closed.pipe(take(1))
            .subscribe(() =>
            {
                this.closeContextMenu();
            });

        this.contextMenuComponentRef.changeDetectorRef.detectChanges();
        this.contextMenuComponentRef.instance.open();
    }

    private handleContextMenuAction(action: ContextMenuAction): void
    {
        const row = action.row;
        const id = row.Id;

        this.$gridSelectionService.SetSelectedIds(this.providerId, [id]);

        switch (action.type)
        {
            case "feature-action":
                this.executeFeatureAction(row);
                break;
            case "view":
            case "edit":
                this.editFromContext(row);
                break;
            case "delete":
                if (this.useRepository())
                {
                    row["$$Deleting"] = true;
                    this.doRepositoryFunction("Delete", { id: id, model: row["$$model"] });
                }
                break;
            case "copy":
                this.doRepositoryFunction("Copy", { id: id, model: row["$$model"] });
                break;
            case "action":
                this.executeAction(row, action.action);
                break;
        }

        this.closeContextMenu();
    }

    private closeContextMenu(): void
    {
        if (this.contextMenuComponentRef)
        {
            this.contextMenuComponentRef.destroy();
            this.contextMenuComponentRef = null;
        }
        this.$gridSelectionService.SetSelectedIds(this.providerId, []);
    }

    hasFeatureAction(row: any): boolean
    {
        return !Utilities.IsNullOrUndefined(row?.FeatureAction) && !row.FeatureAction.Hidden;
    }

    columnPinned(event: IPinColumnEventArgs): void
    {
        this.saveGridState(true);
    }

    activeNodeChange(event: IActiveNodeChangeEventArgs): void
    {
        const self = this;
        if (Utilities.IsNullOrUndefined(event))
        {
            this.activeHeaderField = undefined;
            const headerIconContainers = this.grid.nativeElement.querySelectorAll(".igx-grid-thead__item .igx-grid-th__icons");
            this.grid.visibleColumns.forEach(column =>
            {
                if (headerIconContainers)
                {
                    const headerIconContainer = headerIconContainers[column.visibleIndex];
                    if (headerIconContainer)
                    {
                        const sort = headerIconContainer.querySelector(".sort-icon");
                        sort?.removeEventListener("keydown", this.mimicIconButtonPressed);

                        const filter = headerIconContainer.querySelector(".grid-filter-icon")?.parentElement;
                        filter?.removeEventListener("keydown", this.mimicIconButtonPressed);
                    }
                }
            });

            return;
        }

        removeTabstops();
        this.activeHeaderField = undefined;

        if (event?.tag === "headerCell")
        {
            const headerIconContainers = this.grid.nativeElement.querySelectorAll(".igx-grid-thead__item .igx-grid-th__icons");
            let headerIconContainer = null;
            const headerColumn = this.grid.visibleColumns[event.column];
            this.activeHeaderField = headerColumn.field;

            headerIconContainers.forEach(header =>
            {
                if ((header.previousElementSibling.children[0] as HTMLElement).title === headerColumn.header)
                {
                    headerIconContainer = header;
                    return;
                }
            });

            if (headerIconContainer)
            {
                if (headerColumn.sortable === true)
                {
                    const sort = headerIconContainer.querySelector(".sort-icon");
                    if (sort)
                    {
                        sort.setAttribute("tabindex", "0");
                        sort.setAttribute("role", "button");
                        sort.setAttribute("aria-label", `sort ${headerColumn.header}`);
                        sort.addEventListener("keydown", this.mimicIconButtonPressed);
                    }
                }

                if (headerColumn.filterable === true)
                {
                    const filter = headerIconContainer.querySelector(".grid-filter-icon").parentElement;
                    if (filter)
                    {
                        filter.setAttribute("tabindex", "0");
                        filter.setAttribute("role", "button");
                        filter.setAttribute("aria-label", `filter ${headerColumn.header}`);
                        filter.addEventListener("keydown", this.mimicIconButtonPressed);
                    }
                }

                const selector = headerIconContainer.parentElement.querySelector(".column-move-selector");
                selector.setAttribute("tabindex", "0");
                selector.setAttribute("role", "region");
                selector.addEventListener("keydown", this.mimicIconButtonPressed);
            }
        }

        function removeTabstops(): void
        {
            const headerCells = document.querySelectorAll(".igx-grid-thead__item .igx-grid-th__icons");
            headerCells.forEach(x =>
            {
                let el = x.querySelector(".sort-icon");
                el?.removeAttribute("tabindex");
                el?.removeAttribute("role");
                el?.removeAttribute("aria-label");
                el?.removeEventListener("keydown", self.mimicIconButtonPressed);

                el = x.querySelector(".grid-filter-icon");
                el?.parentElement?.removeAttribute("tabindex");
                el?.parentElement?.removeAttribute("role");
                el?.parentElement?.removeAttribute("aria-label");
                el?.removeAttribute("aria-label");
                el?.removeEventListener("keydown", self.mimicIconButtonPressed);

                const selector = x.parentElement.querySelector(".column-move-selector");
                selector.removeAttribute("tabindex");
                selector.removeAttribute("role");
                selector.removeEventListener("keydown", self.mimicIconButtonPressed);
            });
        }
    }

    mimicIconButtonPressed(event: KeyboardEvent): void
    {
        if (event.key === "Enter" || event.key === " ")
        {
            (event.target as HTMLElement).click();
            event.preventDefault();
            event.stopImmediatePropagation();
        }
    }

    getRenderInfo(column: any): string
    {
        if (column.DataType === "boolean")
            return "boolean";
        if (column.IsIcon)
            return "icon";

        if (column.IsCellAction)
        {
            if (column.LookupType)
                return "actionLookup";
            if (this.formatAction(column))
                return "actionFormatted";
            return "actionDefault";
        }

        if (column.LookupType)
            return "lookup";
        if (column.IsCurrency)
            return "currency";
        if (column.DataType === "number" || column.DataType === "nonDelineatedNumber")
            return "number";
        if (column.DataType === "percent")
            return "percent";
        if (column.DataType === "date")
            return "date";
        if (column.DataType === "dateTime")
            return "dateTime";
        if (column.DataType === "time")
            return "time";
        if (column.DataType === "time24")
            return "time24";
        return "default";
    }

    showNaturalLanguagePrompt(): boolean
    {
        return this.viewMetadata().ShowNaturalLanguagePrompt;
    }

    get promptFilter(): string
    {
        return this.$gridQueryService.prompt;
    }

    set promptFilter(value: string)
    {
        if (value)
        {
            this.$gridQueryService.prompt = value;
            this.$gridQueryService.buildFilterQueryParam(this.viewMetadata());
        }
        else
            this.clearPromptFilter();
    }

    clearPromptFilter(): void
    {
        this.$gridQueryService.filterQuery = undefined;
    }

    searchKeyDown(event: KeyboardEvent): void
    {
        if (event.key !== "Enter")
            return;
        event.preventDefault();
        event.stopPropagation();
        this.applyPromptFilter();
    }

    applyPromptFilter(): void
    {
        this.$metadataPropertyService.recalculateProperties(
            this.model().$$TypeName,
            this.model().$applyProperty,
            this.model(),
            this.model().$$parent,
            this.model()[this.model().$applyProperty.Name],
            undefined,
            true,
            true,
            this.datacontroller()
        ).finally(() => this.$gridQueryService.clearQuery());
    }

    public rowClasses = {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        'last-viewed-row': (row: RowType) => this.$providerStorageService.LastEditedRecordIndex[this.providerId] === row.index
    };
}
